import {
  freshTicket,
  positionBetween,
  PRIORITY_COLOR,
  ISSUE_TYPE_COLOR,
  STATE_COLOR,
  TICKET_STATE_COLOR,
  formatStateLabel,
  resolveAssignees
} from '../../functions/tickets/ticketUtils.js'
import { getSDK } from '../../../shared/functions/sdk.js'
import { applyMarkdownToElement } from '../../../shared/components/MarkdownPreview.js'
// Ticket surfaces use demoteHeadings:true — page chrome owns h1/h2/h3.
const renderMarkdown = (el, body, emptyNode, people) =>
  applyMarkdownToElement(el, body, emptyNode, { demoteHeadings: true, people })

// Fetch ticket_dependencies for one ticket + resolve both sides into
// thin display rows. Returns { outgoing, incoming } where:
//   outgoing = what THIS ticket depends on (blocked_by / waits_on_ship / blocks=out / related)
//   incoming = what depends on THIS ticket (other side of the edge)
const _fetchTicketDeps = async (ticketId) => {
  if (!ticketId) return { outgoing: [], incoming: [] }
  try {
    // SDK route returns either:
    //   1. Pre-shaped { outgoing, incoming } (preferred — server resolves both sides)
    //   2. A flat list of dependency rows that we shape locally (legacy fallback)
    const sdk = getSDK()
    const result = await sdk.execute('workspaceProject.ticketDependencies', 'list', { ticketId })
    if (result && Array.isArray(result.outgoing) && Array.isArray(result.incoming)) {
      return { outgoing: result.outgoing, incoming: result.incoming }
    }
    // Fallback: server returned the raw rows; resolve other-side ticket details
    // via the tickets list route.
    const rows = Array.isArray(result) ? result : (result?.rows || result?.data || [])
    const outRows = rows.filter(r => String(r.ticket_id) === String(ticketId))
    const inRows = rows.filter(r => String(r.depends_on_ticket_id) === String(ticketId))
    const ids = new Set()
    for (const r of outRows) if (r.depends_on_ticket_id) ids.add(r.depends_on_ticket_id)
    for (const r of inRows) if (r.ticket_id) ids.add(r.ticket_id)
    if (!ids.size) return { outgoing: [], incoming: [] }
    const tickets = await sdk.execute('workspaceProject.tickets', 'list', {
      filter: { id_in: Array.from(ids) },
      limit: 200
    })
    const ticketRows = Array.isArray(tickets) ? tickets : (tickets?.rows || tickets?.data || [])
    const byId = new Map()
    for (const t of ticketRows) byId.set(t.id, t)
    const shape = (row, side) => {
      const otherId = side === 'out' ? row.depends_on_ticket_id : row.ticket_id
      const t = byId.get(otherId)
      return t ? { id: t.id, external_id: t.external_id, type: t.type, title: t.title, state: t.state, priority: t.priority, assignee_email: t.assignee_email, kind: row.kind } : null
    }
    return {
      outgoing: outRows.map(r => shape(r, 'out')).filter(Boolean),
      incoming: inRows.map(r => shape(r, 'in')).filter(Boolean)
    }
  } catch (err) {
    console.error('[fetchTicketDeps]', err)
    return { outgoing: [], incoming: [] }
  }
}

// Guard: track IDs currently being hydrated to prevent reactive loops.
const _hydrating = new Set()

// Hydrate a selectedTicket that is missing body (opened from a list that used a
// partial select). Merges the full row into the existing selectedTicket so
// already-visible metadata (title, state, priority) stays on screen while the
// fetch is in-flight. Does NOT clobber with an optimistic stub.
const _hydrateBody = async (root, id) => {
  if (!root || !id) return
  try {
    const result = await getSDK().execute('workspaceProject.tickets', 'get', { id })
    const t = result?.data || result?.row || result
    if (!t || !t.id) return
    root.update({ selectedTicket: {
      ...root.selectedTicket,
      ...t,
      columnKey: t.column_key,
      issueType: t.issue_type,
      epicName: t.epic_name,
      assigneeEmail: t.assignee_email,
      reporterEmail: t.reporter_email,
      updatedAt: t.updated_at,
      createdAt: t.created_at
    } })
  } catch (_) {}
  finally { _hydrating.delete(id) }
}

// Propagate root state update to child elements with imperative onUpdate hooks.
// v3.14 storeUpdate only fires onUpdate on the element attached to the store root;
// DependenciesSection and the sidebar Dependencies panel are not auto-notified
// when selectedTicket changes via a setInterval or async path (#4620).
const _propagateDepsUpdate = (modalEl) => {
  const depSection = modalEl?.Window?.ViewContent?.Content?.Main?.DependenciesSection
  if (depSection && typeof depSection.update === 'function') depSection.update({})
  const sidebarDeps = modalEl?.Window?.ViewContent?.Content?.Sidebar?.Dependencies
  if (sidebarDeps && typeof sidebarDeps.update === 'function') sidebarDeps.update({})
}

// Walk up the parent chain to find the TicketsDetailModal element from any
// descendant (e.g. a TicketDependencyRow click handler). Returns null if the
// element's root does not have a modal in the expected position.
const _findModalFromEl = (el) => {
  try {
    // Try via root element (fastest path when modal is rooted at app root)
    const rootEl = el.getRoot?.()
    if (rootEl?.TicketsDetailModal) return rootEl.TicketsDetailModal
    // Fallback: walk __parent chain looking for the modal key
    let cur = el.__parent
    while (cur) {
      if (cur.__name === 'TicketsDetailModal' || cur.key === 'TicketsDetailModal') return cur
      cur = cur.__parent
    }
  } catch (_) {}
  return null
}

const _openTicketInModal = async (root, id, modalEl) => {
  if (!root || !id) return
  // Optimistic stub so modal swaps immediately; the select=* fetch fills
  // the rest.
  root.update({ selectedTicket: { id } })
  // v3.14 gap: propagate to child onUpdate hooks (#4620).
  if (modalEl) setTimeout(() => _propagateDepsUpdate(modalEl), 0)
  // Snapshot the intent so we can guard against close-mid-flight:
  // if the user closes the modal while the fetch is in-flight,
  // root.selectedTicket becomes null. After the await we must not
  // re-open the modal by overwriting null with the stale hydrated row.
  const openedId = id
  try {
    const result = await getSDK().execute('workspaceProject.tickets', 'get', { id })
    const t = result?.data || result?.row || result
    if (!t || !t.id) return
    // Close-mid-flight guard: abort if user closed the modal during fetch.
    if (!root.selectedTicket || String(root.selectedTicket.id) !== String(openedId)) return
    root.update({ selectedTicket: {
      ...t,
      columnKey: t.column_key,
      issueType: t.issue_type,
      epicName: t.epic_name,
      assigneeEmail: t.assignee_email,
      reporterEmail: t.reporter_email,
      updatedAt: t.updated_at,
      createdAt: t.created_at
    } })
    // After full hydration, propagate again so dep-fetch sees the real id.
    if (modalEl) setTimeout(() => _propagateDepsUpdate(modalEl), 0)
  } catch (_) {}
}

// Credit metering for ticket-ai operations now happens server-side
// inside the ai-chat / ticket-ai edge functions — see
// server/packages/credit-meter. This stub keeps the call sites
// (refId/prompt/response capture for telemetry) intact for now.
const _burnTicketAi = (_root, _payload) => {}

// --- mode helpers ---

const _isNew = (root) => root?.selectedTicket?._new === true
const _isEditing = (root) => root?.ticketEditMode === true || _isNew(root)
// The real ticket object (null when creating new)
const _ticket = (root) => {
  if (_isNew(root)) return null
  const t = root?.selectedTicket
  if (!t) return null
  // Normalize snake_case API fields to camelCase if needed
  if (t.column_key !== undefined && t.columnKey === undefined) {
    t.columnKey = t.column_key
    t.issueType = t.issue_type
    t.epicName = t.epic_name
    t.assigneeEmail = t.assignee_email
    t.reporterEmail = t.reporter_email
    t.updatedAt = t.updated_at
    t.createdAt = t.created_at
  }
  return t
}
const _draft = (root) => root?.ticketDraft || {}

// ── URL sync: ?ticket=<id> ──
// Set to true while _close() is executing so that the display reactive prop
// (which fires synchronously inside root.update via v3.14 flushEffects) cannot
// re-write ?ticket=N after _clearTicketUrl already stripped it.
let _suppressPush = false

const _pushTicketUrl = (id) => {
  if (!id || typeof window === 'undefined') return
  // Guard 1: closing in progress — _clearTicketUrl already ran; do not
  // re-write the URL with the stale selectedTicket value that flushEffects
  // may still see before the null update propagates.
  if (_suppressPush) return
  // Guard 2: idempotent — URL already carries the exact target ticket;
  // skip replaceState to avoid spurious history entries on re-renders.
  if (new URL(window.location).searchParams.get('ticket') === String(id)) return
  const url = new URL(window.location)
  url.searchParams.set('ticket', id)
  window.history.replaceState(null, '', url)
}

const _clearTicketUrl = () => {
  if (typeof window === 'undefined') return
  const url = new URL(window.location)
  if (url.searchParams.has('ticket')) {
    url.searchParams.delete('ticket')
    window.history.replaceState(null, '', url)
  }
}

const _getTicketIdFromUrl = () => {
  if (typeof window === 'undefined') return null
  return new URL(window.location).searchParams.get('ticket')
}

const _close = (root) => {
  _suppressPush = true
  _clearTicketUrl()
  root.update({
    selectedTicket: null,
    ticketDraft: null,
    ticketEditMode: false
  })
  // Release the push-suppress after the synchronous flushEffects cycle
  // completes inside root.update. Any re-open after this point must push.
  Promise.resolve().then(() => { _suppressPush = false })
}

const _cancelEdit = (root) => {
  if (_isNew(root)) _close(root)
  else root.update({ ticketEditMode: false, ticketDraft: null })
}

// Fields relevant per issue type. Unlisted fields always show in view mode;
// in edit mode only the fields for the selected type appear.
const _TYPE_FIELDS = {
  bug:          ['priority', 'assignee', 'column', 'labels', 'reporter', 'dueDate'],
  feature:      ['priority', 'assignee', 'column', 'labels', 'reporter', 'dueDate', 'acceptance', 'storyPoints'],
  'user-story': ['priority', 'assignee', 'column', 'labels', 'dueDate', 'acceptance', 'storyPoints'],
  subtask:      ['assignee', 'column', 'storyPoints'],
  marketplace:  ['assignee', 'labels', 'acceptance']
}
const _showField = (root, field) => {
  if (!_isEditing(root)) return true // view mode: show if value present
  const type = _draft(root).issueType || ''
  if (!type) return true // no type selected: show all
  const fields = _TYPE_FIELDS[type]
  return fields ? fields.includes(field) : true
}

// AI prompt → update ticket draft via SDK ai-chat route.
const _aiUpdateTicket = async (root, prompt, el) => {
  const draft = _draft(root)
  root.update({ _aiTicketLoading: true })

  const systemPrompt = `You update ticket fields based on the user prompt. Return ONLY a valid JSON object with the fields to update. Valid fields: title, body, priority (low/medium/high/urgent/critical), issueType (bug/feature/user-story/subtask/marketplace), acceptanceCriteria, stepsToReproduce, expectedResult, storyPoints (string 0-13), dueDate (YYYY-MM-DD), columnKey, assigneeEmail. Only include fields you want to change. Do NOT include unchanged fields.`

  const userMsg = `Current ticket draft:\n${JSON.stringify(draft, null, 2)}\n\nUser prompt: ${prompt}`

  // Hoisted so the trailing _burnTicketAi() call can read it after the try.
  let full = ''

  try {
    // SDK route forwards to /workspace/ai/chat which returns parsed JSON
    // (non-streaming). Pull the text from common shapes — { text }, { content },
    // { message }, or a plain string — and feed it into the same JSON-extract
    // path we used for the SSE stream.
    const result = await (el?.sdk || getSDK()).execute('workspaceProject.aiChat', 'rpc', {
      payload: {
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMsg }
        ]
      }
    })
    if (typeof result === 'string') full = result
    else if (result?.text) full = result.text
    else if (result?.content) full = String(result.content)
    else if (result?.message) full = typeof result.message === 'string' ? result.message : (result.message.content || '')
    else if (result?.data?.text) full = result.data.text
    else if (result?.data) full = typeof result.data === 'string' ? result.data : JSON.stringify(result.data)

    // Parse JSON from AI response (balance braces to handle trailing text)
    const jsonMatch = full.match(/\{[\s\S]*\}/)
    if (jsonMatch) {
      let parsed = null
      try { parsed = JSON.parse(jsonMatch[0]) } catch {
        let depth = 0, end = 0
        for (let i = 0; i < jsonMatch[0].length; i++) {
          if (jsonMatch[0][i] === '{') depth++
          if (jsonMatch[0][i] === '}') { depth--; if (depth === 0) { end = i + 1; break } }
        }
        try { parsed = JSON.parse(jsonMatch[0].slice(0, end)) } catch {}
      }
      if (parsed) {
        _patchDraft(root, parsed)
        console.log('[_aiUpdateTicket] applied:', Object.keys(parsed))
      }
    } else {
      console.warn('[_aiUpdateTicket] no JSON in response:', full.slice(0, 200))
    }
  } catch (err) {
    console.error('[_aiUpdateTicket]', err)
  }
  _burnTicketAi(root, {
    refId: `ticket-update:${root?.selectedTicket?.id || 'new'}:${Date.now()}`,
    prompt: userMsg,
    response: full
  })
  root.update({ _aiTicketLoading: false })
}

const _patchDraft = (root, patch) =>
  root.update({ ticketDraft: { ..._draft(root), ...patch } })

// Factory: rich-text editor field replicating the comment ComposerRow pattern.
// Shows contenteditable with WysiwygToolbar on focus, saves to draft on blur.
const _richField = (title, draftKey, placeholder, minH, showFn) => ({
  display: (el, s) => showFn(s) ? 'flex' : 'none',
  flow: 'y', gap: 'Z',
  CaptionTitle: { Text: { text: title } },
  EditorRow: {
    display: (el, s) => _isEditing(s.root) ? 'flex' : 'none',
    flow: 'y', gap: '0', round: 'A', overflow: 'hidden', theme: 'field',
    Editor: {
      tag: 'div',
      contenteditable: 'true',
      spellcheck: 'true',
      minHeight: minH,
      maxHeight: 'G',
      width: '100%',
      padding: 'A',
      fontSize: 'Z2',
      lineHeight: '1.5',
      color: 'title',
      background: 'transparent',
      outline: 'none',
      overflow: 'auto',
      wordBreak: 'break-word',
      attr: { 'data-rt-editor': draftKey, 'data-placeholder': placeholder },
      text: (el, s) => _draft(s.root)[draftKey] || '',
      onCreate: (el) => {
        if (!el.node) return
        if (!document.querySelector('style[data-rt-ph]')) {
          const st = document.createElement('style')
          st.textContent = '[data-rt-editor]:empty::before { content: attr(data-placeholder); color: var(--color-caption); pointer-events: none; }'
          st.setAttribute('data-rt-ph', '')
          document.head.appendChild(st)
        }
      },
      onFocus: (e, el) => {
        const tb = el.parent?.BottomBar?.Toolbar?.node
        if (tb) tb.style.display = 'flex'
      },
      onBlur: (e, el, s) => {
        setTimeout(() => {
          if (document.activeElement === el.node) return
          const tb = el.parent?.BottomBar?.Toolbar?.node
          if (tb) tb.style.display = 'none'
          _patchDraft(s.root, { [draftKey]: (el.node.innerText || '').trim() })
        }, 200)
      }
    },
    BottomBar: {
      flow: 'x', align: 'center space-between', padding: '0 Z Z Z',
      Toolbar: { extends: 'WysiwygToolbar', display: 'none', flex: '1', padding: 'V 0 0 V' }
    }
  },
  Body: {
    display: (el, s) => _isEditing(s.root) ? 'none' : 'block',
    tag: 'div', padding: 'A', round: 'Z2',
    background: 'white.04', borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
    fontSize: 'Z2', lineHeight: '1.6',
    wordBreak: 'break-word',
    // Reactive text prop drives two distinct states:
    //   1. "Loading…" — only when selectedTicket.body is undefined, meaning the
    //      partial-select fetch is still in-flight (_hydrateBody not yet done).
    //   2. renderMarkdown path — once body is defined, treat draftKey as '' when
    //      the column doesn't exist in the DB (e.g. acceptanceCriteria is not a
    //      real column — undefined means empty, not loading).
    //
    // IMPORTANT (v3.14): after renderMarkdown calls el.set() to mount child nodes,
    // we return null (not ''). In v3.14 the text effect only writes textContent when
    // val != null; returning '' would write an empty string and wipe the children
    // that el.set() just mounted. null skips the write and leaves children intact.
    color: (el, s) => {
      const t = s.root.selectedTicket
      return (!t || (draftKey === 'body' && t.body === undefined)) ? 'caption' : 'subtitle'
    },
    text: (el, s) => {
      const t = s.root.selectedTicket
      // Ticket not yet loaded at all — show Loading…
      if (!t) {
        el._lastMd = undefined
        return 'Loading…'
      }
      // body column is undefined → hydration still in-flight (partial list fetch)
      if (draftKey === 'body' && t.body === undefined) {
        el._lastMd = undefined
        return 'Loading…'
      }
      // For structured fields that have no DB column (e.g. acceptanceCriteria),
      // t[draftKey] is undefined after hydration — treat as empty string.
      const val = t[draftKey] !== undefined ? t[draftKey] : ''
      // Only clear the stale "Loading…" text node when the markdown content has
      // actually changed.  Unconditional clearing caused a race: textContent was
      // wiped to '' and then renderMarkdown returned early (el._lastMd === body)
      // leaving the description visually empty (TASK#4477 / QA fail #2).
      // Guard: clear only when renderMarkdown will actually re-render (new value).
      if (el.node && el._lastMd !== (val || '')) el.node.textContent = ''
      renderMarkdown(el, val || '', { tag: 'span', text: '' }, s.root.people)
      // Return null so v3.14 does NOT overwrite textContent and wipe el.set() children.
      return null
    }
  }
})

// Multi-assignee helpers — stores comma-separated emails in assigneeEmail
const _draftAssignees = (root) => {
  const raw = _draft(root).assigneeEmail || ''
  return raw.split(',').map(e => e.trim()).filter(Boolean)
}
const _ticketAssigneeEmails = (root) => {
  const t = root?.selectedTicket
  if (!t) return []
  const raw = t.assigneeEmail || ''
  return raw.split(',').map(e => e.trim()).filter(Boolean)
}
const _addAssignee = (root, email) => {
  const cur = _draftAssignees(root)
  if (cur.includes(email)) return
  _patchDraft(root, { assigneeEmail: [...cur, email].join(', ') })
  root.update({ _assigneeQuery: '' })
}
const _popAssignee = (root) => {
  const cur = _draftAssignees(root)
  if (!cur.length) return
  cur.pop()
  _patchDraft(root, { assigneeEmail: cur.join(', ') })
}

// ── Approval helpers ──────────────────────────────────────────────────────────

// Ticket needs a human approval decision when:
//   • state === 'awaiting_approval', OR
//   • labels[] includes 'human-approval-needed'
const _isAwaitingApproval = (root) => {
  const t = _ticket(root)
  if (!t) return false
  return (
    t.state === 'awaiting_approval' ||
    (Array.isArray(t.labels) && t.labels.includes('human-approval-needed'))
  )
}

// Remove a label value from a labels array (returns new array).
const _removeLabel = (labels, value) =>
  (Array.isArray(labels) ? labels : []).filter(l => l !== value)

// Add a label value if not already present (returns new array).
const _addLabel = (labels, value) => {
  const arr = Array.isArray(labels) ? labels : []
  return arr.includes(value) ? arr : [...arr, value]
}

// Optimistically patch the selectedTicket + myTickets arrays in root state.
const _optimisticTicketPatch = (root, patch) => {
  const t = _ticket(root)
  if (!t) return
  const updated = { ...root.selectedTicket, ...patch }
  root.update({
    selectedTicket: updated,
    myTickets: (root.myTickets || []).map(x => x.id === t.id ? { ...x, ...patch } : x)
  })
}

// Full approval flow:
//   1. Insert ticket_comment (audit trail — audit first)
//   2. PATCH ticket state + labels + metadata
// Optimistic: button shows spinner immediately; reverts on any error.
const _approveTicket = async (root, note) => {
  const t = _ticket(root)
  if (!t) return
  const email = root.currentUserEmail || 'nika.tomadze@gmail.com'
  const body = (note || 'Approved.').trim() || 'Approved.'
  const now = new Date().toISOString()

  // Idempotence guard: disable button while pending
  if (root._approvalPending) return
  root.update({ _approvalPending: true })

  // Optimistic UI update
  const prevState = { state: t.state, labels: t.labels, metadata: t.metadata }
  const newLabels = _addLabel(_removeLabel(t.labels, 'human-approval-needed'), 'human-approval-given')
  _optimisticTicketPatch(root, {
    state: 'in_progress',
    labels: newLabels,
    metadata: { ...(t.metadata || {}), approvedAt: now, approvedBy: email }
  })

  // Optimistic comment
  const optimisticComment = { ticket_id: t.id, author_email: email, body, created_at: now, _optimistic: true }
  root.update({ _ticketComments: [...(root._ticketComments || []), optimisticComment] })

  try {
    // Step 1: insert comment (audit trail first) — migrated 2026-04-27 to
    // SDK route workspaceProject.ticketComments.create
    await getSDK().execute('workspaceProject.ticketComments', 'create', {
      ticketId: t.id,
      payload: { author_email: email, body, created_at: now }
    })

    // Step 2: PATCH ticket
    const patchBody = {
      state: 'in_progress',
      labels: newLabels,
      metadata: { ...(t.metadata || {}), approvedAt: now, approvedBy: email },
      updated_at: now
    }
    await getSDK().execute('workspaceProject.tickets', 'update', {
      id: t.id,
      payload: patchBody
    })

    // Replace optimistic comment with confirmed one (remove _optimistic flag)
    root.update({
      _ticketComments: (root._ticketComments || []).map(c =>
        c === optimisticComment ? { ...c, _optimistic: false } : c
      ),
      _approvalPending: false,
      _approvalDoneAt: now,
      _approvalKind: 'approved'
    })
  } catch (err) {
    console.error('[approveTicket]', err)
    // Revert optimistic changes
    _optimisticTicketPatch(root, prevState)
    root.update({
      _ticketComments: (root._ticketComments || []).filter(c => c !== optimisticComment),
      _approvalPending: false,
      _approvalError: err?.message || 'Approval failed'
    })
    // Auto-clear error toast after 4 s
    setTimeout(() => root.update({ _approvalError: null }), 4000)
  }
}

// Reject flow — same structure; state stays in_progress, labels get 'human-approval-rejected'.
const _rejectTicket = async (root, note) => {
  const t = _ticket(root)
  if (!t) return
  const email = root.currentUserEmail || 'nika.tomadze@gmail.com'
  const body = (note || 'Needs changes.').trim() || 'Needs changes.'
  const now = new Date().toISOString()

  if (root._approvalPending) return
  root.update({ _approvalPending: true })

  const prevState = { state: t.state, labels: t.labels, metadata: t.metadata }
  const newLabels = _addLabel(
    _removeLabel(_removeLabel(t.labels, 'human-approval-needed'), 'human-approval-given'),
    'human-approval-rejected'
  )
  _optimisticTicketPatch(root, {
    state: 'in_progress',
    labels: newLabels,
    metadata: { ...(t.metadata || {}), rejectedAt: now, rejectedBy: email }
  })

  const optimisticComment = { ticket_id: t.id, author_email: email, body, created_at: now, _optimistic: true }
  root.update({ _ticketComments: [...(root._ticketComments || []), optimisticComment] })

  try {
    // Step 1: comment first — migrated 2026-04-27 to SDK route
    // workspaceProject.ticketComments.create
    await getSDK().execute('workspaceProject.ticketComments', 'create', {
      ticketId: t.id,
      payload: { author_email: email, body, created_at: now }
    })

    // Step 2: PATCH
    const patchBody = {
      state: 'in_progress',
      labels: newLabels,
      metadata: { ...(t.metadata || {}), rejectedAt: now, rejectedBy: email },
      updated_at: now
    }
    await getSDK().execute('workspaceProject.tickets', 'update', {
      id: t.id,
      payload: patchBody
    })

    root.update({
      _ticketComments: (root._ticketComments || []).map(c =>
        c === optimisticComment ? { ...c, _optimistic: false } : c
      ),
      _approvalPending: false,
      _approvalDoneAt: now,
      _approvalKind: 'rejected'
    })
  } catch (err) {
    console.error('[rejectTicket]', err)
    _optimisticTicketPatch(root, prevState)
    root.update({
      _ticketComments: (root._ticketComments || []).filter(c => c !== optimisticComment),
      _approvalPending: false,
      _approvalError: err?.message || 'Rejection failed'
    })
    setTimeout(() => root.update({ _approvalError: null }), 4000)
  }
}

// --- comment posting (shared by Comment + AI Update modes) ---
const _postComment = async (root, body, el) => {
  const t = _ticket(root)
  if (!t || !body) return
  const email = root.currentUserEmail
  const mode = root._commentTab || 'comment'
  const userName = root._standupUserName || email?.split('@')[0]
  const now = new Date().toISOString()

  if (mode === 'update') {
    // ── Real AI update via Gemini (ai-chat endpoint) ──
    const userComment = { ticket_id: t.id, author_email: email, body: `> ${body}`, created_at: now }
    const aiPlaceholder = { ticket_id: t.id, author_email: 'ai@symbols.app', body: '✦ Thinking...', created_at: new Date(Date.now() + 500).toISOString(), _name: 'AI' }
    const prev = root._ticketComments || []
    root.update({ _ticketComments: [...prev, { ...userComment, _name: userName }, aiPlaceholder] })

    const people = (root.people || []).map(p => `${p.name} <${p.email}>`).join(', ')
    const columns = (root.columns || []).map(c => `${c.key}="${c.label}"`).join(', ')

    const currentUser = (root.people || []).find(p => p.email === email)
    const currentUserName = currentUser?.name || email

    const prompt = `You are a ticket management AI. Given a ticket and a user instruction, return ONLY a valid JSON object with updates.

CURRENT USER: ${currentUserName} <${email}>

TICKET:
- ID: #${t.number || t.id}
- Title: "${t.title || ''}"
- Description: "${(t.body || '').slice(0, 500)}"
- Status column_key: ${t.columnKey || t.column_key || 'unknown'}
- Priority: ${t.priority || 'none'}
- Assignee: ${t.assigneeEmail || t.assignee_email || 'unassigned'}

AVAILABLE COLUMNS: ${columns}
TEAM MEMBERS: ${people}

USER SAYS: "${body}"

Return ONLY valid JSON with fields to change (omit or set null for unchanged):
{"body":"new description or null","column_key":"column key or null","priority":"low/medium/high/critical or null","assignee_email":"email or null","title":"new title or null","summary":"one line what you did"}

Rules:
- "me" or "myself" = the CURRENT USER (${email})
- "assign to X" = find the matching person's SINGLE email from TEAM MEMBERS. Only ONE assignee allowed — pick the first if multiple requested.
- "enhance/improve description" = rewrite the current description to be more detailed and clear
- "mark as done/complete" = set column_key to the Done column
- "move to review" = set column_key to the In Review column
- "add to description: X" = append X to existing description (keep old content)
- Only return fields that actually change. Do NOT return null fields.
- ALWAYS include "summary" field`

    try {
      // SDK route forwards to /workspace/ai/chat which returns parsed JSON
      // (non-streaming). Normalize the result into `full` so the JSON-extract
      // path below stays unchanged.
      const result = await (el?.sdk || getSDK()).execute('workspaceProject.aiChat', 'rpc', {
        payload: { messages: [{ role: 'user', content: prompt }] }
      })
      let full = ''
      if (typeof result === 'string') full = result
      else if (result?.text) full = result.text
      else if (result?.content) full = String(result.content)
      else if (result?.message) full = typeof result.message === 'string' ? result.message : (result.message.content || '')
      else if (result?.data?.text) full = result.data.text
      else if (result?.data) full = typeof result.data === 'string' ? result.data : JSON.stringify(result.data)

      // Credit burn — inline ticket AI prompt (comments bar). Server
      // ledger is authoritative; this is a heartbeat.
      _burnTicketAi(root, {
        refId: `ticket-ai-comment:${t.id || t.number || 'new'}:${Date.now()}`,
        prompt,
        response: full
      })

      // Parse JSON from AI
      let ai
      try { const m = full.match(/\{[\s\S]*\}/); ai = m ? JSON.parse(m[0]) : null } catch { ai = null }

      if (!ai) {
        const comments = root._ticketComments || []
        root.update({ _ticketComments: comments.map(c => c === aiPlaceholder ? { ...c, body: full || 'Could not parse AI response.' } : c) })
        return
      }

      // Apply updates
      const updates = {}
      const actions = []
      if (ai.body != null) { updates.body = ai.body; actions.push('Updated description') }
      if (ai.title != null) { updates.title = ai.title; actions.push(`Title → "${ai.title}"`) }
      if (ai.column_key != null) {
        updates.columnKey = ai.column_key; updates.column_key = ai.column_key
        const col = (root.columns || []).find(c => c.key === ai.column_key)
        actions.push(`Moved to ${col?.label || ai.column_key}`)
      }
      if (ai.priority != null) { updates.priority = ai.priority; actions.push(`Priority → ${ai.priority}`) }
      if (ai.assignee_email != null) {
        // Only single assignee supported — take first email if comma-separated
        let assignEmail = ai.assignee_email
        if (assignEmail.includes(',')) assignEmail = assignEmail.split(',')[0].trim()
        updates.assigneeEmail = assignEmail
        updates.assignee_email = assignEmail
        const person = (root.people || []).find(p => p.email === assignEmail)
        actions.push(`Assigned to ${person?.name || assignEmail}`)
      }

      // Apply to state — include updated timestamp
      updates.updated_at = now
      updates.updatedAt = now
      root.update({
        selectedTicket: { ...root.selectedTicket, ...updates },
        myTickets: (root.myTickets || []).map(x => x.id === t.id ? { ...x, ...updates } : x)
      })

      // Save to Supabase — direct update (reliable)
      {
        const row = { updated_at: now }
        if (updates.body !== undefined) row.body = updates.body
        if (updates.title !== undefined) row.title = updates.title
        if (updates.column_key) row.column_key = updates.column_key
        if (updates.priority) row.priority = updates.priority
        if (updates.assignee_email) row.assignee_email = updates.assignee_email
        // migrated 2026-04-27 to SDK route workspaceProject.tickets.update
        try {
          await getSDK().execute('workspaceProject.tickets', 'update',
            { id: t.id, payload: row })
        } catch (err) {
          console.error('[AI Update] ticket save error:', err?.message || err)
        }
      }
      if (updates.column_key) { try { el.call('moveTicket', { ticketId: t.id, columnKey: updates.column_key, position: 0 }) } catch {} }

      // Update AI comment
      const summary = ai.summary || actions.join(', ')
      const aiBody = `✦ **Done.** ${summary}\n${actions.map(a => `• ${a}`).join('\n')}`
      const comments = root._ticketComments || []
      root.update({ _ticketComments: comments.map(c => c === aiPlaceholder ? { ...c, body: aiBody } : c) })
      // migrated 2026-04-27 to SDK route workspaceProject.ticketComments.create
      // (AI comment + user comment posted together — fire-and-forget; failures
      // are tolerated since the in-memory state already reflects the updates).
      try {
        getSDK().execute('workspaceProject.ticketComments', 'create',
          { ticketId: t.id, payload: userComment }).catch(() => {})
        getSDK().execute('workspaceProject.ticketComments', 'create', {
          ticketId: t.id,
          payload: { author_email: 'ai@symbols.app', body: aiBody, created_at: now }
        }).catch(() => {})
      } catch {}

    } catch (err) {
      console.error('[AI Update]', err?.message || err)
      const comments = root._ticketComments || []
      root.update({ _ticketComments: comments.map(c => c === aiPlaceholder ? { ...c, body: `AI update failed: ${err?.message || 'Unknown error'}` } : c) })
    }
    return
  }

  // ── Below is the old regex fallback — kept as dead code in case AI is unreachable
  if (false) {
    // AI Update mode: interpret the instruction and update the ticket
    const instruction = body.toLowerCase().trim()

    // Parse what the user wants to update
    const updates = {}
    const actions = []

    // Description updates
    if (instruction.includes('description') || instruction.includes('add') || instruction.includes('update') || instruction.includes('change')) {
      // If short instruction, use it as the new description content
      // If it says "add", append to existing
      const currentBody = t.body || ''
      if (instruction.startsWith('add ') || instruction.includes('add to')) {
        const newContent = body.replace(/^add\s+(to\s+)?(the\s+)?(description|desc|body)\s*:?\s*/i, '').trim()
        updates.body = currentBody ? currentBody + '\n\n' + newContent : newContent
        actions.push(`Added to description: "${newContent.slice(0, 50)}${newContent.length > 50 ? '...' : ''}"`)
      } else {
        const newContent = body.replace(/^(update|change|set|write|replace)\s+(the\s+)?(description|desc|body)\s*(to\s*:?\s*|:?\s*)/i, '').trim()
        if (newContent && newContent !== body.trim()) {
          updates.body = newContent
          actions.push(`Updated description`)
        } else {
          updates.body = body
          actions.push(`Set description`)
        }
      }
    }

    // Status updates (use both camelCase for saveTicketToSupabase and snake_case for state)
    let newColumnKey = null
    if (instruction.includes('done') || instruction.includes('complete') || instruction.includes('finish')) {
      newColumnKey = 'eede5560'
      actions.push('Moved to Done')
    } else if (instruction.includes('review') || instruction.includes('ready for review')) {
      newColumnKey = '821cd85d'
      actions.push('Moved to In Review')
    } else if (instruction.includes('progress') || instruction.includes('start') || instruction.includes('working')) {
      newColumnKey = '4d6ab783'
      actions.push('Moved to In Progress')
    } else if (instruction.includes('backlog') || instruction.includes('later')) {
      newColumnKey = 'backlog'
      actions.push('Moved to Backlog')
    }
    if (newColumnKey) {
      updates.columnKey = newColumnKey
      updates.column_key = newColumnKey
    }

    // Priority updates
    if (instruction.includes('urgent') || instruction.includes('critical')) {
      updates.priority = 'critical'
      actions.push('Priority set to Critical')
    } else if (instruction.includes('high priority')) {
      updates.priority = 'high'
      actions.push('Priority set to High')
    } else if (instruction.includes('low priority')) {
      updates.priority = 'low'
      actions.push('Priority set to Low')
    }

    // If no specific action was parsed, treat the whole text as description update
    if (!actions.length) {
      const cleaned = body.replace(/^(update|change|set|add|write)\s+(the\s+)?(description|desc|body|title|ticket)\s*(to\s*:?\s*|:?\s*)/i, '').trim()
      updates.body = (t.body ? t.body + '\n\n' : '') + (cleaned || body)
      actions.push(`Added to description`)
    }

    // Apply updates to ticket
    const updatedTicket = { ...root.selectedTicket, ...updates }
    root.update({ selectedTicket: updatedTicket })

    // Also update in myTickets array
    root.update({
      myTickets: (root.myTickets || []).map(x =>
        x.id === t.id ? { ...x, ...updates } : x
      )
    })

    // Save to Supabase — use saveTicketToSupabase (camelCase) + direct update as backup
    try {
      el.call('saveTicketToSupabase', { ...t, ...updates })
    } catch {
      // Fallback: direct Supabase update with snake_case
      try {
        const row = {}
        if (updates.body !== undefined) row.body = updates.body
        if (newColumnKey) row.column_key = newColumnKey
        if (updates.priority) row.priority = updates.priority
        row.updated_at = new Date().toISOString()
        // migrated 2026-04-27 to SDK route workspaceProject.tickets.update
        getSDK().execute('workspaceProject.tickets', 'update',
          { id: t.id, payload: row }).catch(() => {})
      } catch {}
    }

    // Post AI confirmation comment
    const summary = `✦ **Updated ticket #${t.number || t.id}:**\n${actions.map(a => `• ${a}`).join('\n')}`
    const aiComment = {
      ticket_id: t.id,
      author_email: 'ai@symbols.app',
      body: summary,
      created_at: new Date(Date.now() + 500).toISOString()
    }
    const userComment = { ticket_id: t.id, author_email: email, body: `> ${body}`, created_at: now }
    const prev = root._ticketComments || []
    root.update({
      _ticketComments: [...prev, { ...userComment, _name: userName }, { ...aiComment, _name: 'AI' }]
    })
    // migrated 2026-04-27 to SDK route workspaceProject.ticketComments.create
    try {
      getSDK().execute('workspaceProject.ticketComments', 'create',
        { ticketId: t.id, payload: userComment }).catch(() => {})
      getSDK().execute('workspaceProject.ticketComments', 'create',
        { ticketId: t.id, payload: aiComment }).catch(() => {})
    } catch {}

    // Move ticket if status changed
    if (newColumnKey) {
      try {
        el.call('moveTicket', { ticketId: t.id, columnKey: newColumnKey, position: 0 })
      } catch {}
    }
  } // end dead code

  // Regular comment mode
  const userComment = { ticket_id: t.id, author_email: email, body, created_at: now }
  const prev = root._ticketComments || []
  root.update({ _ticketComments: [...prev, { ...userComment, _name: userName }] })
  // migrated 2026-04-27 to SDK route workspaceProject.ticketComments.create
  try {
    await getSDK().execute('workspaceProject.ticketComments', 'create',
      { ticketId: t.id, payload: userComment })
  } catch (err) {
    console.error('[postComment] insert failed:', err?.message || err)
  }
}

// --- formatting ---

const _formatDate = (d) => {
  if (!d) return ''
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (_) {
    return String(d)
  }
}

const _columnLabel = (root) => {
  const t = _ticket(root)
  if (!t) return ''
  const col = (root.columns || []).find((c) => c.key === t.columnKey)
  return col?.label || t.columnKey || ''
}

// --- save (handles both create and update) ---

const _save = (root, el) => {
  const d = _draft(root)
  // For new tickets using inline editing, grab description from contenteditable
  if (_isNew(root)) {
    const descEl = document.querySelector('[data-desc-body]')
    if (descEl && descEl.innerText?.trim()) d.body = descEl.innerText.trim()
  }
  if (!(d.title || '').trim()) return

  if (_isNew(root)) {
    const sameCol = (root.tickets || []).filter(
      (t) => t.columnKey === d.columnKey
    )
    const lastPos = sameCol.length
      ? Math.max(...sameCol.map((t) => t.position || 0))
      : 0
    const t = {
      id: Date.now(),
      title: d.title.trim(),
      body: d.body || '',
      columnKey: d.columnKey || 'backlog',
      position: positionBetween(lastPos, null),
      assigneeEmail: d.assigneeEmail || null,
      reporterEmail: root.currentUserEmail,
      labels: d.labels || [],
      priority: d.priority || 'medium',
      issueType: d.issueType || null,
      storyPoints: d.storyPoints || null,
      acceptanceCriteria: d.acceptanceCriteria || null,
      dueDate: d.dueDate || null,
      agentTest: d.agentTest !== false // default true
    }
    const pending = root.pendingSuggestion
    root.update({
      tickets: [...(root.tickets || []), t],
      selectedTicket: null,
      ticketDraft: null,
      ticketEditMode: false,
      pendingSuggestion: null
    })
    el.call('saveTicketToSupabase', t).then((realId) => {
      if (!realId) return
      root.update({
        tickets: (root.tickets || []).map((x) =>
          x.id === t.id ? { ...x, id: realId } : x
        )
      })
      if (pending?.bucket === 'tickets' && pending?.item) {
        el.call('markSuggestionApplied', pending.bucket, pending.item, realId)
      }
    })
  } else {
    const t = _ticket(root)
    if (!t) return
    const updated = {
      ...t,
      title: d.title.trim(),
      body: d.body || '',
      columnKey: d.columnKey || t.columnKey,
      priority: d.priority || t.priority,
      assigneeEmail: d.assigneeEmail || null
    }
    root.update({
      tickets: (root.tickets || []).map((x) => (x.id === t.id ? updated : x)),
      selectedTicket: updated,
      ticketDraft: null,
      ticketEditMode: false
    })
    el.call('saveTicketToSupabase', updated)
  }
}

export const TicketsDetailModal = {
  extends: 'Modal',
  stateKey: 'selectedTicket',
  background: 'black.65',
  backdropFilter: 'blur(8px)',
  // Defense-in-depth: even if the `display` reactive prop evaluates to 'flex'
  // before selectedTicket is set (DOMQL first-render race), pointer-events:none
  // prevents the full-viewport overlay from blocking page chrome.
  // When selectedTicket is truthy (modal intentionally open) we restore pe:auto.
  pointerEvents: (el, s) => (s.root?.selectedTicket ? 'auto' : 'none'),

  onClick: (e, el, s) => {
    if (e.target !== el.node) return
    _close(s.root)
  },

  onCreate: (el, s) => {
    // URL-restore: open the modal when the page is loaded with ?ticket=<id>.
    // Runs ONCE on mount (onCreate, not onRender) so we never spawn more than
    // one setInterval per mount — the root cause of the renderer freeze (#4594).
    const urlId = _getTicketIdFromUrl()
    if (!urlId || s.root?.selectedTicket) return

    // Normalize snake_case API fields to camelCase if needed
    const _norm = (t) => {
      if (!t || t.columnKey !== undefined) return t // already normalized
      return {
        ...t,
        columnKey: t.column_key ?? t.columnKey,
        issueType: t.issue_type ?? t.issueType,
        epicName: t.epic_name ?? t.epicName,
        assigneeEmail: t.assignee_email ?? t.assigneeEmail,
        updatedAt: t.updated_at ?? t.updatedAt,
        createdAt: t.created_at ?? t.createdAt,
        reporterEmail: t.reporter_email ?? t.reporterEmail
      }
    }

    const tryRestore = () => {
      try {
        const root = s.root || {}
        // Expand triage buckets (used on /tickets/general) alongside the flat
        // arrays used on other pages. Defensive defaults for every bucket so
        // a missing key never throws and we fall through cleanly.
        const triage = root.triage || {}
        const allTickets = [
          ...(root.tickets || []),
          ...(root.recentTickets || []),
          ...(root.myTickets || []),
          // canvas page stores tickets in canvasAllTickets — include it so that
          // ?ticket=N deep-links work on /tickets/canvas (fix for #4417)
          ...(root.canvasAllTickets || []),
          // /tickets/general triage buckets — fix for #4594 freeze
          ...(triage.now || []),
          ...(triage.ongoing || []),
          ...(triage.blocked || []),
          ...(triage.upcoming || []),
          ...(triage.pendingApproval || []),
          ...(triage.readyToTest || []),
          // decisions bucket — type=decision ASK-USER tickets (#4637)
          ...(triage.decisions || [])
        ]
        const found = allTickets.find(
          (t) => String(t.number) === urlId || String(t.id) === urlId
        )
        if (found) {
          s.root.update({ selectedTicket: _norm({ ...found }) })
          // v3.14 reactive gap: storeUpdate only fires onUpdate on the element
          // directly attached to the state store (the root). Child elements
          // like DependenciesSection and the sidebar Dependencies panel have
          // imperative onUpdate hooks that are NOT auto-called when root state
          // changes via a setInterval closure. Explicitly propagate so the
          // dep-fetch fires and DependenciesSection becomes visible (#4620).
          setTimeout(() => _propagateDepsUpdate(el), 0)
          return true
        }
      } catch (err) {
        console.warn('[TicketsDetailModal] tryRestore error (safe):', err)
      }
      return false
    }

    if (!tryRestore()) {
      // Guard: clear any previous interval from a prior mount (should not happen,
      // but protects against hot-reload / SSR double-mount edge cases).
      if (el._restoreInterval) {
        clearInterval(el._restoreInterval)
        el._restoreInterval = null
      }
      let attempts = 0
      el._restoreInterval = setInterval(() => {
        if (tryRestore() || ++attempts > 20) {
          clearInterval(el._restoreInterval)
          el._restoreInterval = null
          // If no local array contained the ticket after 5 s, fetch it
          // directly rather than silently clearing the URL — this handles the
          // case where the page's own data store (e.g. canvasAllTickets) is
          // still null/empty because the async fetch hasn't resolved yet, or
          // the ticket is not in any currently-loaded list.
          if (attempts > 20 && !s.root?.selectedTicket) {
            const numId = parseInt(urlId, 10)
            const fetchId = !isNaN(numId) ? numId : urlId
            _openTicketInModal(s.root || el.getRoot(), fetchId, el)
          }
        }
      }, 250)
    }
  },

  onDestroy: (el) => {
    // Always clear the restore interval on unmount so it cannot fire
    // after the element is gone (stale closure → renderer hang).
    if (el._restoreInterval) {
      clearInterval(el._restoreInterval)
      el._restoreInterval = null
    }
  },

  // Override display to also sync URL (runs reactively on selectedTicket changes)
  display: (el, s) => {
    const ticket = s.root?.selectedTicket
    if (ticket) {
      if (!ticket._new && (ticket.number || ticket.id))
        _pushTicketUrl(ticket.number || ticket.id)
      // Hydrate body when ticket was opened from a list fetch that omitted body.
      // body===undefined means the field was not in the select; null/'' = truly empty.
      // _hydrating guard prevents reactive loops (display fires again on each update).
      if (ticket.id && !ticket._new && ticket.body === undefined && !_hydrating.has(ticket.id)) {
        _hydrating.add(ticket.id)
        const root = s.root || el.getRoot()
        _hydrateBody(root, ticket.id)
      }
      return 'flex'
    }
    // Only clear URL if no pending restore (check URL directly)
    if (!_getTicketIdFromUrl()) _clearTicketUrl()
    return 'none'
  },

  Window: {
    width: '1080px',
    maxWidth: '95vw',
    height: '88vh',
    maxHeight: '88vh',
    minHeight: '640px',
    overflow: 'hidden',
    position: 'relative',

    // Animated inner-shadow glow when AI is loading
    AiLoadingOverlay: {
      position: 'absolute',
      inset: '0',
      zIndex: '50',
      pointerEvents: 'none',
      round: 'inherit',
      display: (el, s) => s.root?._aiTicketLoading ? 'block' : 'none',
      onCreate: (el) => {
        if (!el.node) return
        if (!document.querySelector('style[data-ai-glow]')) {
          const st = document.createElement('style')
          st.setAttribute('data-ai-glow', '')
          st.textContent = `
            @keyframes ai-glow-pulse {
              0%, 100% { box-shadow: inset 0 0 60px 10px var(--color-blue); opacity: 0.2; }
              50% { box-shadow: inset 0 0 120px 30px var(--color-cyan); opacity: 0.5; }
            }
            [data-key="AiLoadingOverlay"] { animation: ai-glow-pulse 2s ease-in-out infinite; }
          `
          document.head.appendChild(st)
        }
      }
    },

    ModalHeader: {
      Icons: {
        Close: {
          extends: 'IconButton',
          zIndex: '10',
          Icon: { icon: 'x' },
          onClick: (e, el, s) => _close(s.root)
        },
        EditBtn: {
          extends: 'IconButton',
          hide: (el, s) => _isEditing(s.root) || _isNew(s.root),
          title: 'Edit ticket',
          Icon: { icon: 'edit' },
          onClick: (e, el, s) => {
            const t = _ticket(s.root)
            if (!t) return
            s.root.update({
              ticketEditMode: true,
              ticketDraft: {
                title: t.title || '',
                body: t.body || '',
                columnKey: t.columnKey || t.column_key || 'backlog',
                priority: t.priority || 'medium',
                assigneeEmail: t.assigneeEmail || t.assignee_email || ''
              }
            })
          }
        },
        DeleteBtn: {
          extends: 'IconButton',
          hide: (el, s) => _isEditing(s.root) || _isNew(s.root),
          title: 'Delete ticket',
          Icon: { icon: 'trash' },
          onClick: (e, el, s) => {
            const t = _ticket(s.root)
            if (!t) return
            s.root.update({
              confirmAction: {
                title: `Delete "${t.title || 'ticket'}"?`,
                message: 'This permanently removes the ticket. This cannot be undone.',
                confirmText: 'Delete',
                icon: 'trash',
                onConfirm: () => {
                  s.root.update({
                    tickets: (s.root.tickets || []).filter((x) => x.id !== t.id),
                    selectedTicket: null,
                    ticketDraft: null,
                    ticketEditMode: false
                  })
                  el.call('deleteTicketFromSupabase', t.id)
                }
              }
            })
          }
        }
      },
      Hgroup: {
        TitleInput: {
          if: (el, s) => _isEditing(s.root),
          tag: 'input',
          type: 'text',
          placeholder: 'What needs to be done?',
          fontSize: 'B',
          fontWeight: '600',
          color: 'title',
          fontFamily: 'inherit',
          width: '100%',
          background: 'transparent',
          borderWidth: '0',
          outline: 'none',
          value: (el, s) => _draft(s.root).title || '',
          onInput: (e, el, s) => _patchDraft(s.root, { title: e.target.value })
        },
        H: {
          if: (el, s) => !_isEditing(s.root),
          tag: 'h6',
          fontWeight: '600',
          color: 'title',
          fontFamily: 'inherit',
          width: '100%',
          text: (el, s) => _ticket(s.root)?.title || 'Ticket'
        },
        P: {
          if: (el, s) => !_isNew(s.root) && (_isEditing(s.root) || !!_ticket(s.root)),
          flow: 'x',
          align: 'center left',
          gap: 'Z',
          flexWrap: 'wrap',
          text: (el, s) =>
            _isEditing(s.root)
              ? _ticket(s.root)?.title || ''
              : '',

          RepoLink: {
            if: (el, s) => !_isEditing(s.root) && s.root.selectedTicket?.repository,
            flow: 'x',
            align: 'center left',
            gap: 'Y',
            Icon: { icon: 'github', boxSize: 'Z2' },
            Text: { tag: 'span', text: (el, s) => s.root.selectedTicket?.repository || '' }
          },

          NumberSep: {
            if: (el, s) => !_isEditing(s.root) && s.root.selectedTicket?.repository && s.root.selectedTicket?.number,
            tag: 'span', opacity: '.5', text: '·'
          },

          NumberLabel: {
            if: (el, s) => !_isEditing(s.root) && s.root.selectedTicket?.number,
            tag: 'span', color: 'subtitle', fontWeight: '500',
            text: (el, s) => s.root.selectedTicket?.number ? `#${s.root.selectedTicket.number}` : ''
          },

          UpdatedSep: {
            if: (el, s) => !_isEditing(s.root) && s.root.selectedTicket?.updatedAt && (s.root.selectedTicket?.repository || s.root.selectedTicket?.number),
            tag: 'span', opacity: '.5', text: '·'
          },

          Updated: {
            if: (el, s) => !_isEditing(s.root) && s.root.selectedTicket?.updatedAt,
            tag: 'span',
            text: (el, s) => `updated ${_formatDate(s.root.selectedTicket?.updatedAt)}`
          }
        }
      }
    },

    // ── View content — also used for new tickets (inline editing) ──

    ViewContent: {
      flex: '1 1 0',
      flow: 'y',
      minHeight: '0',
      overflow: 'hidden',

      Hero: null,

      Content_Chips: {
        flow: 'x',
        gap: 'Z',
        flexWrap: 'wrap',
        align: 'center left',
        padding: '0 0 Z 0',
        flex: '0 0 auto',

          IssueTypeSelect: {
            display: (el, s) => _isEditing(s?.root) ? 'inline-flex' : 'none',
            tag: 'select',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.06em',
            border: 'none',
            cursor: 'pointer',
            color: (el, s) => ISSUE_TYPE_COLOR[_draft(s.root).issueType] || 'caption',
            background: (el, s) => `color-mix(in srgb, var(--color-${ISSUE_TYPE_COLOR[_draft(s.root).issueType] || 'caption'}) 16%, transparent)`,
            value: (el, s) => _draft(s.root).issueType || '',
            onInput: (e, el, s) => _patchDraft(s.root, { issueType: e.target.value }),
            children: [
              { tag: 'option', value: '', text: 'Type…' },
              { tag: 'option', value: 'bug', text: 'BUG' },
              { tag: 'option', value: 'feature', text: 'FEATURE' },
              { tag: 'option', value: 'user-story', text: 'USER STORY' },
              { tag: 'option', value: 'subtask', text: 'SUBTASK' },
              { tag: 'option', value: 'marketplace', text: 'MARKETPLACE' }
            ]
          },
          IssueType: {
            display: (el, s) =>
              !_isEditing(s?.root) && s?.root?.selectedTicket?.issueType ? 'inline-flex' : 'none',
            tag: 'span',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.06em',
            color: (el, s) => ISSUE_TYPE_COLOR[s.root.selectedTicket?.issueType] || 'caption',
            background: (el, s) => `color-mix(in srgb, var(--color-${ISSUE_TYPE_COLOR[s.root.selectedTicket?.issueType] || 'caption'}) 16%, transparent)`,
            text: (el, s) => (s.root.selectedTicket?.issueType || '').toUpperCase()
          },

          Epic: {
            display: (el, s) =>
              s.root.selectedTicket?.epicName ? 'inline-flex' : 'none',
            tag: 'span',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '600',
            color: 'blue',
            background:
              'color-mix(in srgb, var(--color-blue) 16%, transparent)',
            text: (el, s) =>
              `EPIC · ${(s.root.selectedTicket?.epicName || '').toUpperCase()}`
          },

          State: {
            display: (el, s) =>
              s.root.selectedTicket?.state ? 'inline-flex' : 'none',
            flow: 'x',
            align: 'center center',
            gap: 'V',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.06em',
            color: (el, s) =>
              TICKET_STATE_COLOR[s.root.selectedTicket?.state] || STATE_COLOR[s.root.selectedTicket?.state] || 'caption',
            background: (el, s) =>
              `color-mix(in srgb, var(--color-${TICKET_STATE_COLOR[s.root.selectedTicket?.state] || STATE_COLOR[s.root.selectedTicket?.state] || 'caption'}) 16%, transparent)`,
            Dot: null,
            Text: {
              tag: 'span',
              text: (el, s) => formatStateLabel(s.root.selectedTicket?.state)
            }
          },

          PrioritySelect: {
            display: (el, s) => _isEditing(s?.root) ? 'inline-flex' : 'none',
            tag: 'select',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '700',
            border: 'none',
            cursor: 'pointer',
            color: (el, s) => PRIORITY_COLOR[_draft(s.root).priority] || 'caption',
            background: (el, s) => `color-mix(in srgb, var(--color-${PRIORITY_COLOR[_draft(s.root).priority] || 'caption'}) 16%, transparent)`,
            value: (el, s) => _draft(s.root).priority || 'medium',
            onInput: (e, el, s) => _patchDraft(s.root, { priority: e.target.value }),
            children: [
              { tag: 'option', value: 'low', text: 'LOW' },
              { tag: 'option', value: 'medium', text: 'MEDIUM' },
              { tag: 'option', value: 'high', text: 'HIGH' },
              { tag: 'option', value: 'urgent', text: 'URGENT' },
              { tag: 'option', value: 'critical', text: 'CRITICAL' }
            ]
          },
          PriorityChip: {
            display: (el, s) => {
              const p = !_isEditing(s?.root) && s?.root?.selectedTicket?.priority
              return p ? 'inline-flex' : 'none'
            },
            tag: 'span',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.04em',
            color: (el, s) => PRIORITY_COLOR[s.root.selectedTicket?.priority] || 'caption',
            background: (el, s) => `color-mix(in srgb, var(--color-${PRIORITY_COLOR[s.root.selectedTicket?.priority] || 'caption'}) 16%, transparent)`,
            text: (el, s) => (s.root.selectedTicket?.priority || '').toUpperCase()
          },

          // ── Approval banner ─────────────────────────────────────────────────────
          // Visible only when ticket is awaiting human approval.
          // Shows Approve + Reject buttons with an optional note textarea.
          // Sequence: comment INSERT (audit trail) → state PATCH.
          ApprovalBar: {
            display: (el, s) => (_isAwaitingApproval(s.root) && !_isEditing(s.root) && !s.root._approvalDoneAt) ? 'flex' : 'none',
            flow: 'x',
            gap: 'Z',
            align: 'center left',
            flexWrap: 'wrap',
            padding: 'Z A',
            round: 'A',
            background: 'orange.08',
            borderWidth: '1px',
            borderColor: 'orange.3',
            borderStyle: 'solid',
            width: '100%',
            marginTop: 'Z',

            Icon: { icon: 'warning', boxSize: 'Z2', color: 'orange', flexShrink: '0' },

            Label: {
              tag: 'span',
              fontSize: 'Z2',
              fontWeight: '600',
              color: 'orange',
              flex: '1',
              minWidth: '120px',
              text: 'Awaiting your approval'
            },

            NoteInput: {
              tag: 'input',
              type: 'text',
              'aria-label': 'Approval note',
              fontSize: 'Z2',
              color: 'title',
              background: 'overlay',
              borderWidth: '1px',
              borderColor: 'line',
              borderStyle: 'solid',
              round: 'Z',
              padding: 'V Z',
              flex: '1',
              minWidth: '160px',
              maxWidth: '280px',
              placeholder: 'Approval note (default: Approved.)',
              value: (el, s) => s.root._approvalNote || '',
              onInput: (e, el, s) => s.root.update({ _approvalNote: e.target.value })
            },

            ApproveBtn: {
              tag: 'button',
              type: 'button',
              padding: 'V A',
              round: 'Z',
              border: 'none',
              cursor: (el, s) => s.root._approvalPending ? 'not-allowed' : 'pointer',
              fontSize: 'Z2',
              fontWeight: '700',
              fontFamily: 'inherit',
              background: (el, s) => s.root._approvalPending ? 'green.08' : 'green.15',
              // green+20 lightens the base #59AC56 to ~#7ac677, achieving ≥4.5:1 on dark green bg (WCAG AA)
              color: 'green+20',
              transition: 'all 120ms ease',
              ':hover': { background: 'green', color: 'white' },
              text: (el, s) => s.root._approvalPending ? 'Approving…' : 'Approve',
              disabled: (el, s) => !!s.root._approvalPending,
              onClick: async (e, el, s) => {
                e.stopPropagation()
                const note = (s.root._approvalNote || '').trim() || 'Approved.'
                await _approveTicket(s.root, note)
                s.root.update({ _approvalNote: '' })
              }
            },

            RejectBtn: {
              tag: 'button',
              type: 'button',
              padding: 'V A',
              round: 'Z',
              border: 'none',
              cursor: (el, s) => s.root._approvalPending ? 'not-allowed' : 'pointer',
              fontSize: 'Z2',
              fontWeight: '700',
              fontFamily: 'inherit',
              background: (el, s) => s.root._approvalPending ? 'red.06' : 'red.12',
              // red+15 lightens the base #EB6650 to ~#f08070, achieving ≥4.5:1 on dark red bg (WCAG AA)
              color: 'red+15',
              transition: 'all 120ms ease',
              ':hover': { background: 'red', color: 'white' },
              text: (el, s) => s.root._approvalPending ? 'Rejecting…' : 'Reject',
              disabled: (el, s) => !!s.root._approvalPending,
              onClick: async (e, el, s) => {
                e.stopPropagation()
                const note = (s.root._approvalNote || '').trim() || 'Needs changes.'
                await _rejectTicket(s.root, note)
                s.root.update({ _approvalNote: '' })
              }
            }
          },

          // ── Post-approval confirmation pill ──────────────────────────────────
          ApprovedPill: {
            display: (el, s) => (s.root._approvalDoneAt && !_isEditing(s.root)) ? 'inline-flex' : 'none',
            flow: 'x',
            align: 'center center',
            gap: 'Y',
            padding: 'Y Z1',
            round: 'C',
            background: (el, s) => (s.root._approvalKind === 'rejected') ? 'red.08' : 'green.08',
            color: (el, s) => (s.root._approvalKind === 'rejected') ? 'red' : 'green',
            fontSize: 'Y2',
            fontWeight: '700',
            letterSpacing: '.04em',
            text: (el, s) => {
              const when = s.root._approvalDoneAt
                ? new Date(s.root._approvalDoneAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : ''
              return (s.root._approvalKind === 'rejected')
                ? `REJECTED · ${when}`
                : `APPROVED · ${when}`
            }
          },

          // ── Error toast ───────────────────────────────────────────────────────
          ApprovalErrorToast: {
            display: (el, s) => s.root._approvalError ? 'inline-flex' : 'none',
            flow: 'x',
            align: 'center center',
            gap: 'Y',
            padding: 'Y Z1',
            round: 'C',
            background: 'red.08',
            color: 'red',
            fontSize: 'Y2',
            fontWeight: '600',
            text: (el, s) => s.root._approvalError || ''
          },

          StatusSelect: {
            display: (el, s) => _isEditing(s?.root) ? 'inline-flex' : 'none',
            tag: 'select',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            color: 'subtitle',
            background: 'overlay',
            value: (el, s) => _draft(s.root).columnKey || 'backlog',
            onInput: (e, el, s) => _patchDraft(s.root, { columnKey: e.target.value }),
            childExtends: {
              tag: 'option',
              value: (el, s) => s.value,
              text: (el, s) => s.text
            },
            childrenAs: 'state',
            children: (el, s) => {
              const cols = s.root.columns || []
              return cols.length
                ? cols.map(c => ({ value: c.key, text: c.label }))
                : [{ value: 'backlog', text: 'Backlog' }, { value: 'todo', text: 'To do' }, { value: 'done', text: 'Done' }]
            }
          },
          Status: {
            display: (el, s) => !_isEditing(s?.root) && _columnLabel(s.root) ? 'inline-flex' : 'none',
            flow: 'x',
            align: 'center center',
            gap: 'Z',
            padding: 'Y Z1',
            round: 'C',
            fontSize: 'Y2',
            fontWeight: '600',
            color: 'subtitle',
            background: 'overlay',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',
            StatusLabel: { tag: 'span', opacity: '.7', text: 'STATUS' },
            Sep: { tag: 'span', opacity: '.4', text: '·' },
            Value: {
              tag: 'span',
              color: 'title',
              fontWeight: '700',
              text: (el, s) => _columnLabel(s.root)
            }
          }
      },

      Content: {
        flow: 'x',
        flex: '1 1 0',
        minHeight: '0',
        overflow: 'hidden',

        Main: {
          flow: 'y',
          gap: 'A1',
          padding: 'A1 B1 - -',
          flex: '1 1 0',
          minWidth: '0',
          overflow: 'hidden auto',

          SubIssues: {
            display: (el, s) =>
              (s.root.selectedTicket?.subIssuesTotal || 0) > 0
                ? 'flex'
                : 'none',
            flow: 'y',
            gap: 'Z',
            padding: 'A',
            round: 'Z2',
            background: 'overlay',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',

            Header: {
              flow: 'x',
              align: 'center space-between',
              gap: 'Z',
              Label: {
                flow: 'x',
                align: 'center left',
                gap: 'Z',
                Icon: { icon: 'checkCircle', boxSize: 'Z2', color: 'caption' },
                Text: {
                  tag: 'span',
                  fontSize: 'Z2',
                  color: 'subtitle',
                  text: (el, s) => {
                    const t = s.root.selectedTicket
                    return `Sub-issues · ${t?.subIssuesCompleted || 0} of ${t?.subIssuesTotal || 0}`
                  }
                }
              },
              Percent: {
                tag: 'span',
                fontSize: 'Y2',
                fontWeight: '600',
                color: 'caption',
                text: (el, s) => {
                  const t = s.root.selectedTicket
                  const tot = t?.subIssuesTotal || 0
                  const done = t?.subIssuesCompleted || 0
                  return tot ? `${Math.round((done / tot) * 100)}%` : ''
                }
              }
            },

            Track: {
              position: 'relative',
              width: '100%',
              height: 'Z',
              round: 'C',
              background: 'overlay',
              overflow: 'hidden',
              Fill: {
                position: 'absolute',
                top: '0',
                left: '0',
                height: '100%',
                round: 'C',
                background:
                  'linear-gradient(90deg, var(--color-green), var(--color-cyan))',
                transition: 'width 200ms ease',
                width: (el, s) => {
                  const t = s.root.selectedTicket
                  const tot = t?.subIssuesTotal || 0
                  const done = t?.subIssuesCompleted || 0
                  return tot ? `${(done / tot) * 100}%` : '0%'
                }
              }
            }
          },

          Description: _richField('Description', 'body', 'Add a description…', 'D', () => true),

          AcceptanceCriteria: _richField('Acceptance Criteria', 'acceptanceCriteria', 'Checklist of conditions that must be met', 'C',
            (s) => _showField(s.root, 'acceptance') && (_isEditing(s.root) || s.root.selectedTicket?.acceptanceCriteria)),

          StepsToReproduce: _richField('Steps to Reproduce', 'stepsToReproduce', '1. Go to page X\n2. Click here\n3. See error', 'B1',
            (s) => {
              const type = _isEditing(s.root) ? (_draft(s.root).issueType || '') : (s.root.selectedTicket?.issueType || '')
              return (type === 'bug' && _isEditing(s.root)) || s.root.selectedTicket?.stepsToReproduce
            }),

          ExpectedResult: _richField('Expected Result', 'expectedResult', 'What should have happened?', 'B1',
            (s) => {
              const type = _isEditing(s.root) ? (_draft(s.root).issueType || '') : (s.root.selectedTicket?.issueType || '')
              return (type === 'bug' && _isEditing(s.root)) || s.root.selectedTicket?.expectedResult
            }),

          Labels: null,

          CustomFields: {
            display: (el, s) => {
              const cf = s.root.selectedTicket?.customFields || {}
              const known = new Set([
                'Title',
                'Status',
                'Priority',
                'Story Points',
                'Estimate',
                'Size',
                'Severity',
                'Environment',
                'Review Status',
                'Iteration',
                'Epic',
                'Due Date'
              ])
              return Object.keys(cf).some((k) => !known.has(k))
                ? 'flex'
                : 'none'
            },
            flow: 'y',
            gap: 'Z',
            CFLabel: {
              tag: 'span',
              fontSize: 'Y2',
              fontWeight: '600',
              letterSpacing: '.06em',
              color: 'caption',
              text: 'OTHER FIELDS'
            },
            Grid: {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 'Z1',
              childExtends: 'TicketsDetailField',
              childrenAs: 'state',
              children: (el, s) => {
                const cf = s.root.selectedTicket?.customFields || {}
                const known = new Set([
                  'Title',
                  'Status',
                  'Priority',
                  'Story Points',
                  'Estimate',
                  'Size',
                  'Severity',
                  'Environment',
                  'Review Status',
                  'Iteration',
                  'Epic',
                  'Due Date'
                ])
                return Object.entries(cf)
                  .filter(([k]) => !known.has(k))
                  .map(([label, value]) => ({ label, value: String(value) }))
              }
            }
          },

          // ── AI Prompt — edit mode: AI updates ticket fields from prompt ──
          AiPrompt: {
            display: (el, s) => _isEditing(s.root) ? 'flex' : 'none',
            flow: 'y',
            gap: 'Z',
            marginTop: 'auto',
            padding: 'A 0 0 0',
            borderTopWidth: '1px',
            borderTopColor: 'line',
            borderTopStyle: 'solid',

            CaptionTitle: { Text: { text: 'AI Assistant' } },

            PromptRow: {
              flow: 'y',
              gap: '0',
              round: 'A',
              overflow: 'hidden',
              theme: 'field',
              position: 'relative',

              Editor: {
                tag: 'div',
                contenteditable: 'true',
                spellcheck: 'true',
                minHeight: 'B1',
                maxHeight: 'D',
                width: '100%',
                padding: 'Z1 A',
                fontSize: 'Z2',
                lineHeight: '1.5',
                color: 'title',
                background: 'transparent',
                outline: 'none',
                overflow: 'auto',
                wordBreak: 'break-word',
                attr: { 'data-ai-prompt': 'true', 'data-placeholder': 'Ask AI to fill fields, improve description, add acceptance criteria…' },
                onCreate: (el) => {
                  if (!el.node) return
                  if (!document.querySelector('style[data-ai-prompt-ph]')) {
                    const style = document.createElement('style')
                    style.textContent = '[data-ai-prompt]:empty::before { content: attr(data-placeholder); color: var(--color-caption); pointer-events: none; }'
                    style.setAttribute('data-ai-prompt-ph', '')
                    document.head.appendChild(style)
                  }
                },
                onKeydown: (e, el, s) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    const prompt = (el.node.innerText || '').trim()
                    if (!prompt) return
                    el.node.innerHTML = ''
                    _aiUpdateTicket(s.root, prompt, el)
                  }
                }
              },

              BottomBar: {
                flow: 'x',
                align: 'center flex-end',
                padding: '0 Z Z Z',

                SendBtn: {
                  extends: 'CircleButton',
                  padding: 'Z',
                  icon: 'magicstar',
                  title: 'Send to AI',
                  onClick: (e, el, s) => {
                    const editor = el.parent?.parent?.Editor?.node
                    if (!editor) return
                    const prompt = (editor.innerText || '').trim()
                    if (!prompt) return
                    editor.innerHTML = ''
                    _aiUpdateTicket(s.root, prompt, el)
                  }
                }
              }
            }
          },

          // ── Dependencies in main column — below description, above comments ──
          // Mirrors the sidebar TicketDependenciesPanel data but renders in the
          // scrollable main column with "Blocked by" / "Blocks" sub-headings.
          // Visible only when current ticket has at least one dependency row.
          // Data is fetched/cached via the same _ticketDeps root cache used by
          // the sidebar panel (populated via TicketDependenciesPanel.onUpdate).
          DependenciesSection: {
            display: (el, s) => {
              if (_isEditing(s.root)) return 'none'
              const id = s.root?.selectedTicket?.id
              const d = s.root?._ticketDeps?.[id]
              return (d && !d._loading && (d.outgoing.length || d.incoming.length)) ? 'flex' : 'none'
            },
            flow: 'y',
            gap: 'Z',
            padding: 'A',
            round: 'Z2',
            background: 'white.03',
            borderWidth: '1px',
            borderColor: 'line',
            borderStyle: 'solid',

            // Trigger the dep fetch reactively on selectedTicket changes.
            // onRender is one-shot (fires at element creation before selectedTicket is
            // set by the async setInterval URL-restore in TicketsDetailModal.onCreate).
            // onUpdate fires on every root state change, so it catches the moment
            // selectedTicket becomes truthy. el._fetchedForId deduplicates per ticket id.
            onUpdate: (el, s) => {
              const id = s.root?.selectedTicket?.id
              if (!id || el._fetchedForId === id) return
              el._fetchedForId = id
              const cache = s.root._ticketDeps || {}
              if (cache[id] && !cache[id]._loading) return
              s.root._ticketDeps = { ...cache, [id]: { outgoing: [], incoming: [], _loading: true } }
              _fetchTicketDeps(id).then((deps) => {
                if (s.root?.selectedTicket?.id !== id) return
                s.root.update({ _ticketDeps: { ...(s.root._ticketDeps || {}), [id]: deps } })
              })
            },

            SectionHeader: {
              flow: 'x',
              align: 'center left',
              gap: 'Z',
              Icon: { icon: 'link', boxSize: 'Z2', color: 'caption' },
              Label: {
                tag: 'span',
                fontSize: 'Y2',
                fontWeight: '700',
                color: 'caption',
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                text: 'Dependencies'
              },
              Count: {
                tag: 'span',
                fontSize: 'Y2',
                fontWeight: '600',
                color: 'caption',
                opacity: '.6',
                text: (el, s) => {
                  const id = s.root?.selectedTicket?.id
                  const d = s.root?._ticketDeps?.[id]
                  const n = (d?.outgoing?.length || 0) + (d?.incoming?.length || 0)
                  return n ? `· ${n}` : ''
                }
              }
            },

            // "Blocked by" — rows where THIS ticket depends on another
            // (ticket_id = current.id in ticket_dependencies)
            BlockedBy: {
              display: (el, s) => {
                const d = s.root?._ticketDeps?.[s.root.selectedTicket?.id]
                return (d?.outgoing?.length) ? 'flex' : 'none'
              },
              flow: 'y',
              gap: 'Y',
              marginTop: 'Z',

              Label: {
                tag: 'span',
                fontSize: 'Y2',
                fontWeight: '600',
                color: 'red.7',
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                text: (el, s) => {
                  const d = s.root?._ticketDeps?.[s.root.selectedTicket?.id]
                  return `Blocked by · ${d?.outgoing?.length || 0}`
                }
              },

              List: {
                flow: 'y',
                gap: 'V',
                childExtends: 'TicketDependencyMainRow',
                childrenAs: 'state',
                children: (el, s) => s.root?._ticketDeps?.[s.root.selectedTicket?.id]?.outgoing || []
              }
            },

            // "Blocks" — rows where THIS ticket blocks another
            // (depends_on_ticket_id = current.id in ticket_dependencies)
            Blocks: {
              display: (el, s) => {
                const d = s.root?._ticketDeps?.[s.root.selectedTicket?.id]
                return (d?.incoming?.length) ? 'flex' : 'none'
              },
              flow: 'y',
              gap: 'Y',
              marginTop: (el, s) => {
                const d = s.root?._ticketDeps?.[s.root.selectedTicket?.id]
                return d?.outgoing?.length ? 'Z' : '0'
              },

              Label: {
                tag: 'span',
                fontSize: 'Y2',
                fontWeight: '600',
                color: 'orange.8',
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                text: (el, s) => {
                  const d = s.root?._ticketDeps?.[s.root.selectedTicket?.id]
                  return `Blocks · ${d?.incoming?.length || 0}`
                }
              },

              List: {
                flow: 'y',
                gap: 'V',
                childExtends: 'TicketDependencyMainRow',
                childrenAs: 'state',
                children: (el, s) => s.root?._ticketDeps?.[s.root.selectedTicket?.id]?.incoming || []
              }
            }
          },

          // ── Comments — pinned to bottom of content area ──
          Comments: {
            hide: (el, s) => _isEditing(s.root),
            flow: 'y',
            gap: 'Z',
            marginTop: 'auto',
            padding: '0',

            // Comment list — no scroll, grows with content
            CommentList: {
              flow: 'y',
              gap: 'Z',
              padding: 'Z 0',
              childExtends: 'TicketCommentRow',
              childrenAs: 'state',
              children: (el, s) => s.root?._ticketComments || []
            },

            // Composer with mode tabs + toolbar inside
            ComposerRow: {
              flow: 'y',
              gap: '0',
              round: 'A',
              overflow: 'hidden',
              theme: 'field',
              position: 'relative',

              // Mode tabs — top right, matching send button position
              Tabs: {
                position: 'absolute',
                top: 'Z',
                right: 'Z',
                zIndex: '2',
                flow: 'x',
                gap: 'V',

                UpdateTab: {
                  tag: 'button', type: 'button',
                  padding: 'V Z', round: 'C',
                  border: 'none', cursor: 'pointer',
                  fontSize: 'Y1', fontWeight: '600', fontFamily: 'inherit',
                  background: (el, s) => (s.root?._commentTab || 'comment') === 'update' ? 'blue.15' : 'transparent',
                  color: (el, s) => (s.root?._commentTab || 'comment') === 'update' ? 'blue' : 'caption',
                  text: 'Update',
                  onClick: (e, el, s) => s.root.update({ _commentTab: 'update' })
                },

                CommentTab: {
                  tag: 'button', type: 'button',
                  padding: 'V Z', round: 'C',
                  border: 'none', cursor: 'pointer',
                  fontSize: 'Y1', fontWeight: '600', fontFamily: 'inherit',
                  background: (el, s) => (s.root?._commentTab || 'comment') === 'comment' ? 'blue.15' : 'transparent',
                  color: (el, s) => (s.root?._commentTab || 'comment') === 'comment' ? 'blue' : 'caption',
                  text: 'Comment',
                  onClick: (e, el, s) => s.root.update({ _commentTab: 'comment' })
                }
              },

              // Contenteditable editor
              Editor: {
                tag: 'div',
                contenteditable: 'true',
                spellcheck: 'true',
                minHeight: 'C',
                maxHeight: 'E',
                width: '100%',
                padding: 'A',
                fontSize: 'Z2',
                lineHeight: '1.5',
                color: 'title',
                background: 'transparent',
                outline: 'none',
                overflow: 'auto',
                wordBreak: 'break-word',
                attr: {
                  'data-comment-editor': 'true',
                  'data-placeholder': (el, s) => (s.root?._commentTab || 'comment') === 'update'
                    ? 'Describe what changed (AI will summarize)...'
                    : 'Add a comment...'
                },
                onCreate: (el) => {
                  if (!el.node) return
                  const style = document.createElement('style')
                  style.textContent = '[data-comment-editor]:empty::before { content: attr(data-placeholder); color: var(--color-caption); pointer-events: none; }'
                  if (!document.querySelector('style[data-comment-editor-ph]')) {
                    style.setAttribute('data-comment-editor-ph', '')
                    document.head.appendChild(style)
                  }
                },
                onFocus: (e, el, s) => {
                  // Only show toolbar in Comment mode, not Update mode
                  if ((s.root?._commentTab || 'comment') === 'comment') {
                    const toolbar = el.parent?.BottomBar?.CommentToolbar?.node
                    if (toolbar) toolbar.style.display = 'flex'
                  }
                },
                onBlur: (e, el) => {
                  setTimeout(() => {
                    if (document.activeElement === el.node) return
                    const toolbar = el.parent?.BottomBar?.CommentToolbar?.node
                    if (toolbar) toolbar.style.display = 'none'
                  }, 200)
                },
                onKeydown: (e, el, s) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    const body = (el.node.innerText || '').trim()
                    if (!body) return
                    el.node.innerHTML = ''
                    _postComment(s.root, body, el)
                  }
                }
              },

              // Bottom bar: toolbar + send button
              BottomBar: {
                flow: 'x',
                align: 'center space-between',
                padding: '0 Z Z Z',

                CommentToolbar: {
                  extends: 'WysiwygToolbar',
                  display: 'none',
                  flex: '1',
                  padding: 'V 0 0 V'
                },

                SendBtn: {
                  extends: 'CircleButton',
                  padding: 'Z',
                  marginLeft: 'auto',
                  icon: 'chevronUp',
                  Icon: { transform: 'rotate(45deg)' },
                  '@dark': {
                    background: 'white',
                    color: 'title',
                    ':hover': { background: 'gray14', color: 'gray1' },
                    ':active': { background: 'gray13', color: 'gray1' }
                  },
                  onClick: (e, el, s) => {
                    const editor = el.parent?.parent?.Editor?.node
                    if (!editor) return
                    const body = (editor.innerText || '').trim()
                    if (!body) return
                    editor.innerHTML = ''
                    _postComment(s.root, body, el)
                  }
                }
              }
            },

            onUpdate: async (el, s) => {
              const t = s.root?.selectedTicket
              if (!t || el._lastTicketId === t.id) return
              el._lastTicketId = t.id
              // migrated 2026-04-27 to SDK route workspaceProject.ticketComments.list
              try {
                const data = await (el.sdk || getSDK()).execute(
                  'workspaceProject.ticketComments', 'list',
                  { ticketId: t.id, order: 'created_at.asc' })
                if (data) {
                  const people = s.root.people || []
                  s.root.update({
                    _ticketComments: data.map(c => ({
                      ...c,
                      _name: (people.find(p => p.email === c.author_email)?.name || c.author_email || '').split(' ')[0]
                    }))
                  })
                }
              } catch {}
            }
          }
        },

        Sidebar: {
          flow: 'y',
          gap: 'A1',
          padding: 'A1 - - A1',
          width: '280px',
          minWidth: '240px',
          flex: '0 0 auto',
          background: 'overlay',
          borderLeftWidth: '1px',
          borderLeftColor: 'line',
          borderLeftStyle: 'solid',
          overflowY: 'auto',

          // Type, Priority, Column are in Content_Chips — no duplicates here.

          // ── Priority (slider in edit, badge in view) ──
          Priority: {
            display: (el, s) =>
              _showField(s.root, 'priority') && (_isEditing(s.root) || s.root.selectedTicket?.priority) ? 'flex' : 'none',
            flow: 'y', gap: 'Z',
            CaptionTitle: { Text: { text: (el, s) => {
              const v = _isEditing(s.root) ? _draft(s.root).priority : s.root.selectedTicket?.priority
              return `Priority · ${(v || 'medium').charAt(0).toUpperCase() + (v || 'medium').slice(1)}`
            } } },
            Input: {
              display: (el, s) => _isEditing(s.root) ? 'block' : 'none',
              tag: 'input', type: 'range',
              min: '0', max: '4', step: '1',
              width: '100%',
              height: 'Y',
              cursor: 'pointer',
              border: 'none',
              outline: 'none',
              value: (el, s) => String(['low', 'medium', 'high', 'urgent', 'critical'].indexOf(_draft(s.root).priority || 'medium')),
              onInput: (e, el, s) => {
                const vals = ['low', 'medium', 'high', 'urgent', 'critical']
                const colors = ['var(--color-green)', 'var(--color-blue)', 'var(--color-orange)', 'var(--color-red)', 'var(--color-pink)']
                _patchDraft(s.root, { priority: vals[parseInt(e.target.value)] || 'medium' })
                e.target.style.accentColor = colors[parseInt(e.target.value)] || ''
              },
              onCreate: (el, s) => {
                if (!el.node) return
                const idx = ['low', 'medium', 'high', 'urgent', 'critical'].indexOf(_draft(s.root).priority || 'medium')
                const colors = ['var(--color-green)', 'var(--color-blue)', 'var(--color-orange)', 'var(--color-red)', 'var(--color-pink)']
                el.node.style.accentColor = colors[idx] || ''
              }
            },
            Value: {
              display: (el, s) => _isEditing(s.root) ? 'none' : 'block',
              tag: 'span', width: 'fit-content',
              padding: 'V Z', round: 'C', fontSize: 'Y2', fontWeight: '700', letterSpacing: '.04em',
              color: (el, s) => PRIORITY_COLOR[s.root.selectedTicket?.priority] || 'caption',
              background: (el, s) => `color-mix(in srgb, var(--color-${PRIORITY_COLOR[s.root.selectedTicket?.priority] || 'caption'}) 16%, transparent)`,
              text: (el, s) => (s.root.selectedTicket?.priority || '').toUpperCase()
            }
          },

          // ── Assignees — multi-select with chips ──
          Assignees: {
            display: (el, s) =>
              _showField(s.root, 'assignee') || !_isEditing(s.root) ? 'flex' : 'none',
            extends: 'InputField',
            position: 'relative',
            Title: { text: 'Assignees' },
            Input: {
              display: (el, s) => _isEditing(s.root) ? 'block' : 'none',
              placeholder: (el, s) => _draftAssignees(s.root).length ? '' : 'Type a name…',
              value: (el, s) => s.root._assigneeQuery || '',
              onInput: (e, el, s) => s.root.update({ _assigneeQuery: e.target.value }),
              onKeydown: (e, el, s) => {
                if (e.key === 'Enter' || e.key === 'Tab') {
                  const q = (s.root._assigneeQuery || '').trim().toLowerCase()
                  if (!q) return
                  e.preventDefault()
                  const existing = new Set(_draftAssignees(s.root))
                  const match = (s.root.people || []).find(p =>
                    !existing.has(p.email) && (p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
                  )
                  if (match) _addAssignee(s.root, match.email)
                }
                if (e.key === 'Backspace' && !e.target.value) _popAssignee(s.root)
              }
            },
            SelectedChips: {
              display: (el, s) => {
                const list = _isEditing(s.root) ? _draftAssignees(s.root) : _ticketAssigneeEmails(s.root)
                return list.length ? 'flex' : 'none'
              },
              flow: 'x', flexWrap: 'wrap', gap: 'Y', padding: 'Z 0 0 0',
              childExtends: 'TicketsDetailAssignee',
              childrenAs: 'state',
              children: (el, s) => {
                const emails = _isEditing(s.root) ? _draftAssignees(s.root) : _ticketAssigneeEmails(s.root)
                return emails.map(email => {
                  const p = (s.root.people || []).find(pp => pp.email === email)
                  return { key: email, name: p?.name || email, avatar: el.call('avatarUrl', p?.name || email) }
                })
              }
            },
            Suggestions: {
              display: (el, s) => {
                const q = (s.root._assigneeQuery || '').trim()
                return _isEditing(s.root) && q ? 'flex' : 'none'
              },
              position: 'absolute', top: '100%', left: '0',
              zIndex: '200', flow: 'y', gap: '0', width: '100%',
              background: 'overlayStrong', round: 'Z', padding: 'V',
              borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
              boxShadow: '0 4px 12px var(--color-shadow)',
              maxHeight: 'E', overflow: 'auto', textAlign: 'left',
              childExtends: {
                tag: 'button', type: 'button', flow: 'x', align: 'center left', gap: 'Z',
                padding: 'Z A', round: 'V', border: 'none', background: 'transparent',
                color: 'title', cursor: 'pointer', width: '100%', fontSize: 'Z2',
                ':hover': { background: 'line' },
                Avatar: { avatarType: 'initials', boxSize: 'B', round: '50%',
                  src: (el, s) => el.call('avatarUrl', s.name) },
                Info: { flow: 'y', gap: 'V', flex: '1', minWidth: '0',
                  Name: { tag: 'span', fontWeight: '500', text: (el, s) => s.name },
                  Email: { tag: 'span', fontSize: 'Y2', color: 'caption', text: (el, s) => s.email }
                },
                onMousedown: (e, el, s) => {
                  e.preventDefault()
                  _addAssignee(s.root, s.email)
                }
              },
              childrenAs: 'state',
              children: (el, s) => {
                const q = (s.root._assigneeQuery || '').trim().toLowerCase()
                if (!q) return []
                const existing = new Set(_draftAssignees(s.root))
                return (s.root.people || [])
                  .filter(p => !existing.has(p.email) && (
                    p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
                  ))
                  .slice(0, 6)
              }
            }
          },

          // Column is in Content_Chips StatusSelect

          // ── Story Points (slider) ──
          StoryPointsField: {
            display: (el, s) =>
              _showField(s.root, 'storyPoints') && (_isEditing(s.root) || s.root.selectedTicket?.storyPoints != null) ? 'flex' : 'none',
            flow: 'y', gap: 'Z',
            CaptionTitle: { Text: { text: (el, s) => {
              const SP = ['0', '1', '2', '3', '5', '8', '13']
              const v = _isEditing(s.root) ? (_draft(s.root).storyPoints || '') : (s.root.selectedTicket?.storyPoints || '')
              return `Story Points · ${v || '—'}`
            } } },
            Input: {
              display: (el, s) => _isEditing(s.root) ? 'block' : 'none',
              tag: 'input', type: 'range',
              min: '0', max: '6', step: '1',
              width: '100%',
              height: 'Y',
              cursor: 'pointer',
              border: 'none',
              outline: 'none',
              value: (el, s) => {
                const SP = ['0', '1', '2', '3', '5', '8', '13']
                return String(SP.indexOf(_draft(s.root).storyPoints || '0'))
              },
              onInput: (e, el, s) => {
                const SP = ['0', '1', '2', '3', '5', '8', '13']
                const colors = ['var(--color-caption)', 'var(--color-green)', 'var(--color-cyan)', 'var(--color-blue)', 'var(--color-purple)', 'var(--color-orange)', 'var(--color-red)']
                _patchDraft(s.root, { storyPoints: SP[parseInt(e.target.value)] || '0' })
                e.target.style.accentColor = colors[parseInt(e.target.value)] || ''
              },
              onCreate: (el, s) => {
                if (!el.node) return
                const SP = ['0', '1', '2', '3', '5', '8', '13']
                const colors = ['var(--color-caption)', 'var(--color-green)', 'var(--color-cyan)', 'var(--color-blue)', 'var(--color-purple)', 'var(--color-orange)', 'var(--color-red)']
                const idx = SP.indexOf(_draft(s.root).storyPoints || '0')
                el.node.style.accentColor = colors[idx] || ''
              }
            },
            Value: {
              display: (el, s) => _isEditing(s.root) ? 'none' : 'block',
              tag: 'span', color: 'cyan', fontSize: 'B', fontWeight: '700',
              text: (el, s) => String(s.root.selectedTicket?.storyPoints || '')
            }
          },

          // ── Due Date ──
          DueDateField: {
            display: (el, s) =>
              _showField(s.root, 'dueDate') && (_isEditing(s.root) || s.root.selectedTicket?.dueDate) ? 'flex' : 'none',
            extends: 'InputField',
            Title: { text: 'Due date' },
            Input: {
              display: (el, s) => _isEditing(s.root) ? 'block' : 'none',
              type: 'date',
              value: (el, s) => _draft(s.root).dueDate || s.root.selectedTicket?.dueDate || '',
              onInput: (e, el, s) => _patchDraft(s.root, { dueDate: e.target.value })
            },
            Value: {
              display: (el, s) => _isEditing(s.root) ? 'none' : 'flex',
              flow: 'x', align: 'center left', gap: 'Y',
              Icon: { icon: 'clockOutline', boxSize: 'Z2', color: 'caption' },
              Text: { tag: 'span', fontSize: 'Z2', color: 'title',
                text: (el, s) => _formatDate(s.root.selectedTicket?.dueDate) }
            }
          },

          // ── Reporter (autocomplete) ──
          ReporterField: {
            display: (el, s) =>
              _showField(s.root, 'reporter') && (_isEditing(s.root) || s.root.selectedTicket?.reporterEmail) ? 'flex' : 'none',
            extends: 'InputField',
            position: 'relative',
            Title: { text: 'Reporter' },
            Input: {
              display: (el, s) => _isEditing(s.root) ? 'block' : 'none',
              placeholder: 'Type a name…',
              value: (el, s) => {
                const q = s.root._reporterQuery
                if (q != null) return q
                const email = _draft(s.root).reporterEmail || s.root.currentUserEmail || ''
                const p = (s.root.people || []).find(pp => pp.email === email)
                return p?.name || email
              },
              onInput: (e, el, s) => s.root.update({ _reporterQuery: e.target.value }),
              onFocus: (e, el, s) => {
                const email = _draft(s.root).reporterEmail || s.root.currentUserEmail || ''
                const p = (s.root.people || []).find(pp => pp.email === email)
                s.root.update({ _reporterQuery: p?.name || email })
                e.target.select()
              },
              onBlur: (e, el, s) => {
                setTimeout(() => s.root.update({ _reporterQuery: null }), 200)
              }
            },
            Suggestions: {
              display: (el, s) => {
                const q = s.root._reporterQuery
                return _isEditing(s.root) && q && q.length > 0 ? 'flex' : 'none'
              },
              position: 'absolute', top: '100%', left: '0',
              zIndex: '200', flow: 'y', gap: '0', width: '100%',
              background: 'overlayStrong', round: 'Z', padding: 'V',
              borderWidth: '1px', borderColor: 'line', borderStyle: 'solid',
              boxShadow: '0 4px 12px var(--color-shadow)',
              maxHeight: 'E', overflow: 'auto', textAlign: 'left',
              childExtends: {
                tag: 'button', type: 'button', flow: 'x', align: 'center left', gap: 'Z',
                padding: 'Z A', round: 'V', border: 'none', background: 'transparent',
                color: 'title', cursor: 'pointer', width: '100%', fontSize: 'Z2',
                ':hover': { background: 'line' },
                Name: { tag: 'span', fontWeight: '500', text: (el, s) => s.name },
                onMousedown: (e, el, s) => {
                  e.preventDefault()
                  _patchDraft(s.root, { reporterEmail: s.email })
                  s.root.update({ _reporterQuery: null })
                }
              },
              childrenAs: 'state',
              children: (el, s) => {
                const q = (s.root._reporterQuery || '').trim().toLowerCase()
                if (!q) return []
                return (s.root.people || [])
                  .filter(p => p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q))
                  .slice(0, 6)
              }
            },
            Value: {
              display: (el, s) => _isEditing(s.root) ? 'none' : 'block',
              tag: 'span', fontSize: 'Z2', color: 'subtitle',
              text: (el, s) => {
                const email = s.root.selectedTicket?.reporterEmail
                if (!email) return ''
                const p = (s.root.people || []).find(pp => pp.email === email)
                return p?.name || email
              }
            }
          },

          // ── View-only metadata (shown when values exist) ──
          Severity: {
            display: (el, s) => s.root.selectedTicket?.severity ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Severity' } },
            Value: { tag: 'span', color: 'red', fontSize: 'Z2', fontWeight: '600',
              text: (el, s) => s.root.selectedTicket?.severity || '' }
          },
          Size: {
            display: (el, s) => s.root.selectedTicket?.size ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Size' } },
            Value: { tag: 'span', color: 'subtitle', fontSize: 'Z2',
              text: (el, s) => s.root.selectedTicket?.size || '' }
          },
          Estimate: {
            // Guard against a stringified "undefined" reaching us from
            // upstream (GitHub sync sometimes serializes missing
            // estimates as the literal string "undefined").
            display: (el, s) => {
              const e = s.root.selectedTicket?.estimate
              return (e != null && e !== '' && e !== 'undefined') ? 'flex' : 'none'
            },
            CaptionTitle: { Text: { text: 'Estimate' } },
            Value: { tag: 'span', color: 'cyan', fontSize: 'Z2', fontWeight: '600',
              text: (el, s) => {
                const e = s.root.selectedTicket?.estimate
                return (e == null || e === '' || e === 'undefined') ? '' : `${e}h`
              } }
          },
          Iteration: {
            display: (el, s) => s.root.selectedTicket?.iterationTitle ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Iteration' } },
            Value: { tag: 'span', fontSize: 'Z2', fontWeight: '600', color: 'title',
              text: (el, s) => s.root.selectedTicket?.iterationTitle || '' }
          },
          Milestone: {
            display: (el, s) => s.root.selectedTicket?.milestone ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Milestone' } },
            Value: { tag: 'span', fontSize: 'Z2', color: 'title',
              text: (el, s) => s.root.selectedTicket?.milestone || '' }
          },
          Environment: {
            display: (el, s) => s.root.selectedTicket?.environment ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Environment' } },
            Value: { tag: 'span', fontSize: 'Z2', color: 'title',
              text: (el, s) => s.root.selectedTicket?.environment || '' }
          },
          ReviewStatus: {
            display: (el, s) => s.root.selectedTicket?.reviewStatus ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Review' } },
            Value: { tag: 'span', fontSize: 'Z2', color: 'yellow', fontWeight: '600',
              text: (el, s) => s.root.selectedTicket?.reviewStatus || '' }
          },
          Dependencies: { extends: 'TicketDependenciesPanel' },

          Parent: {
            display: (el, s) => s.root.selectedTicket?.parentIssueUrl ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Parent issue' } },
            Value: { tag: 'a',
              attr: { href: (el, s) => s.root.selectedTicket?.parentIssueUrl, target: '_blank', rel: 'noopener' },
              flow: 'x', align: 'center left', gap: 'Y', color: 'cyan', ':hover': { color: 'title' },
              Icon: { icon: 'arrowAngleUp', boxSize: 'Z2' },
              Text: { tag: 'span', fontSize: 'Z2', text: 'Open parent →' } }
          },
          TrackedBy: {
            display: (el, s) => (s.root.selectedTicket?.trackedByCount || 0) > 0 ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Tracked by' } },
            Value: { tag: 'span', fontSize: 'Z2', color: 'title',
              text: (el, s) => `${s.root.selectedTicket?.trackedByCount || 0} issue${s.root.selectedTicket?.trackedByCount === 1 ? '' : 's'}` }
          },
          Updated: {
            display: (el, s) => s.root.selectedTicket?.updatedAt || s.root.selectedTicket?.updated_at ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Last updated' } },
            Value: { tag: 'span', color: 'caption',
              text: (el, s) => _formatDate(s.root.selectedTicket?.updatedAt || s.root.selectedTicket?.updated_at) }
          },
          Created: {
            display: (el, s) => s.root.selectedTicket?.createdAt || s.root.selectedTicket?.created_at ? 'flex' : 'none',
            CaptionTitle: { Text: { text: 'Created' } },
            Value: { tag: 'span', color: 'caption',
              text: (el, s) => _formatDate(s.root.selectedTicket?.createdAt || s.root.selectedTicket?.created_at) }
          }
        }
      }
    },

    // ── Footer ────────────────────────────────────────────────────────────────

    ModalFooter: {
      borderTopWidth: '1px',
      borderTopColor: 'line',
      borderTopStyle: 'solid',
      background: 'transparent',
      flex: '0 0 auto',
      flow: 'x',
      gap: 'Z1',
      align: 'center left',

      // ── Editing mode: Save/Cancel ──
      SaveBtn: {
        hide: (el, s) => !_isEditing(s.root),
        extends: 'Button',
        theme: 'primary',
        text: (el, s) => (_isNew(s.root) ? 'Create ticket' : 'Save changes'),
        onClick: (e, el, s) => _save(s.root, el)
      },
      CancelEditBtn: {
        hide: (el, s) => !_isEditing(s.root) || _isNew(s.root),
        extends: 'Button',
        theme: 'secondary',
        text: 'Cancel',
        onClick: (e, el, s) => {
          s.root.update({ ticketEditMode: false, ticketDraft: null })
        }
      },

      // ── QA agent auto-test toggle (new-ticket form only) ──
      // Writes metadata.agent_test on submit. Default on.
      AgentTestToggle: {
        hide: (el, s) => !_isNew(s.root),
        flow: 'x',
        gap: 'Z',
        align: 'center left',
        marginLeft: 'auto',
        cursor: 'pointer',
        onClick: (e, el, s) => {
          const cur = _draft(s.root).agentTest !== false
          _patchDraft(s.root, { agentTest: !cur })
        },
        Checkbox: {
          tag: 'input',
          type: 'checkbox',
          cursor: 'pointer',
          flexShrink: '0',
          checked: (el, s) => _draft(s.root).agentTest !== false,
          onChange: (e, el, s) => {
            _patchDraft(s.root, { agentTest: e.target.checked })
          }
        },
        Label: {
          tag: 'label',
          fontSize: 'Y2',
          color: 'caption',
          cursor: 'pointer',
          userSelect: 'none',
          text: 'Auto-test by QA agent'
        }
      },

      // ── View mode: State-aware CTA buttons ──
      PrimaryAction: {
        hide: (el, s) => _isEditing(s.root),
        extends: 'Button',
        theme: 'primary',
        text: (el, s) => {
          const t = _ticket(s.root)
          if (!t) return ''
          const col = t.columnKey || t.column_key
          if (col === '4d6ab783') return 'Mark as ready to test'  // In Progress
          if (col === '821cd85d') return 'Approve review'         // In Review
          if (col === '20f135e3') return 'Start QA'               // Ready For QA
          if (col === 'd677f7ee') return 'Mark as done'           // In QA
          return 'Start working'                                   // Backlog/Todo
        },
        onClick: (e, el, s) => {
          const t = _ticket(s.root)
          if (!t) return
          const col = t.columnKey || t.column_key
          let nextCol
          if (col === '4d6ab783') nextCol = '20f135e3'      // In Progress → Ready For QA
          else if (col === '821cd85d') nextCol = '20f135e3'  // In Review → Ready For QA
          else if (col === '20f135e3') nextCol = 'd677f7ee'  // Ready For QA → In QA
          else if (col === 'd677f7ee') nextCol = 'eede5560'  // In QA → Done
          else nextCol = '4d6ab783'                            // Backlog/Todo → In Progress
          el.call('moveTicket', { ticketId: t.id, columnKey: nextCol, position: 0 })
          s.root.update({
            selectedTicket: { ...s.root.selectedTicket, column_key: nextCol, columnKey: nextCol },
            myTickets: (s.root.myTickets || []).map(x =>
              x.id === t.id ? { ...x, column_key: nextCol } : x
            )
          })
        }
      },

      SecondaryAction: {
        hide: (el, s) => {
          if (_isEditing(s.root)) return true
          const t = _ticket(s.root)
          const col = t?.columnKey || t?.column_key
          // Only show "Move for later" on active columns
          return !col || col === 'backlog' || col === 'eede5560'
        },
        extends: 'Button',
        theme: 'secondary',
        text: 'Move for later',
        onClick: (e, el, s) => {
          const t = _ticket(s.root)
          if (!t) return
          el.call('moveTicket', { ticketId: t.id, columnKey: 'backlog', position: 0 })
          s.root.update({
            selectedTicket: { ...s.root.selectedTicket, column_key: 'backlog', columnKey: 'backlog' },
            myTickets: (s.root.myTickets || []).map(x =>
              x.id === t.id ? { ...x, column_key: 'backlog' } : x
            )
          })
        }
      },

      GithubLink: {
        hide: (el, s) =>
          _isEditing(s.root) || !s.root.selectedTicket?.githubUrl,
        tag: 'a',
        attr: {
          href: (el, s) => s.root.selectedTicket?.githubUrl,
          target: '_blank',
          rel: 'noopener'
        },
        flow: 'x',
        align: 'center left',
        gap: 'Z',
        padding: 'Z A',
        round: 'C',
        background: 'overlay',
        color: 'subtitle',
        ':hover': { background: 'line', color: 'title' },
        transition: 'all 120ms ease',
        Icon: { icon: 'github', boxSize: 'Z2' },
        Text: {
          tag: 'span',
          fontSize: 'Z2',
          fontWeight: '500',
          text: 'Open in GitHub'
        }
      },

      Spacer: { flex: '1' },

      AskAIBtn: {
        hide: (el, s) => _isEditing(s.root),
        extends: 'Button',
        theme: 'secondary',
        flow: 'x',
        gap: 'Z',
        align: 'center center',
        Icon: { icon: 'magicstar', boxSize: 'Z2' },
        text: 'Ask AI',
        onClick: (e, el, s) => {
          const t = _ticket(s.root)
          if (!t) return
          s.root.update({
            assistantOpen: true,
            assistantAttachment: {
              type: 'ticket',
              id: t.id,
              title: `#${t.number || t.id} · ${t.title || ''}`,
              body: t.body || '',
              meta: {
                status: t.columnKey || t.column_key,
                assignee: t.assigneeEmail || t.assignee_email,
                epic: t.epicName || t.epic_name,
                repository: t.repository
              }
            }
          })
        }
      },

      MentionBtn: {
        hide: (el, s) => _isEditing(s.root),
        extends: 'Button',
        theme: 'secondary',
        flow: 'x',
        gap: 'Z',
        align: 'center center',
        Icon: { icon: 'chat', boxSize: 'Z2' },
        text: 'Mention',
        onClick: async (e, el, s) => {
          const t = _ticket(s.root)
          if (!t) return
          // Load channels if not already loaded (tickets page doesn't fetch them)
          // migrated 2026-04-27 to SDK routes workspaceProject.chat (channels)
          // and workspaceProject.chat.members (channel_members across all channels).
          if (!(s.root.channels || []).length) {
            try {
              const data = await getSDK().execute('workspaceProject.chat', 'list',
                { order: 'updated_at.desc' })
              if (data) s.root.update({ channels: data })
            } catch {}
            try {
              const data = await getSDK().execute('workspaceProject.chat.members', 'list')
              if (data) s.root.update({ channelMembers: data })
            } catch {}
          }
          s.root.update({
            _mentionModal: {
              open: true,
              ticketId: t.id,
              ticketNumber: t.number || t.id,
              ticketTitle: t.title || '',
              message: `Check out ticket ##${t.number || t.id} — ${t.title || ''}`,
              channelId: null
            }
          })
        }
      }
    }
  }
}

// ── Mention/Share ticket in chat modal ──
export const TicketMentionModal = {
  show: (el, s) => !!(s.root?._mentionModal?.open),
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  zIndex: 10001,
  align: 'center center',
  background: 'black.5',
  onClick: (e, el, s) => {
    if (e.target === el.node) s.root.update({ _mentionModal: { open: false } })
  },

  Card: {
    flow: 'y',
    gap: 'A',
    padding: 'B',
    round: 'A',
    background: 'overlayStrong',
    borderWidth: '1px',
    borderColor: 'line',
    borderStyle: 'solid',
    width: 'H',
    maxWidth: '90vw',
    boxShadow: '0 16px 48px -8px rgba(0, 0, 0, 0.6)',

    Title: {
      tag: 'h3',
      margin: '0',
      fontSize: 'A',
      fontWeight: '600',
      color: 'title',
      text: 'Share in chat'
    },

    // Channel/DM selector
    ChannelField: {
      flow: 'y',
      gap: 'V',

      CaptionTitle: { fontSize: 'Y2', Text: { text: 'SEND TO' } },

      Select: {
        tag: 'select',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z2',
        fontFamily: 'inherit',
        width: '100%',
        value: (el, s) => s.root?._mentionModal?.channelId || '',
        onInput: (e, el, s) => {
          s.root.update({
            _mentionModal: { ...s.root._mentionModal, channelId: e.target.value }
          })
        },
        children: (el, s) => {
          const root = s.root
          if (!root) return []
          const me = root.currentUserEmail
          const channels = (root.channels || []).filter(c => c.kind === 'channel')
          const dms = (root.channels || []).filter(c => c.kind === 'dm' || c.kind === 'group_dm')
          const opts = [{ tag: 'option', value: '', text: 'Select channel or DM...' }]

          if (channels.length) {
            opts.push({ tag: 'option', disabled: true, text: '── Channels ──' })
            for (const c of channels) opts.push({ tag: 'option', value: c.id, text: `# ${c.name}` })
          }
          if (dms.length) {
            opts.push({ tag: 'option', disabled: true, text: '── Direct Messages ──' })
            for (const c of dms) {
              const members = (root.channelMembers || [])
                .filter(m => m.channel_id === c.id && m.member_email !== me)
                .map(m => {
                  const p = (root.people || []).find(pp => pp.email === m.member_email)
                  return p?.name || m.member_email?.split('@')[0] || ''
                })
              opts.push({ tag: 'option', value: c.id, text: members.join(', ') || 'DM' })
            }
          }
          return opts
        }
      }
    },

    // Message editor
    MessageField: {
      flow: 'y',
      gap: 'V',

      CaptionTitle: { fontSize: 'Y2', Text: { text: 'MESSAGE' } },

      Editor: {
        tag: 'textarea',
        rows: '3',
        padding: 'Z A',
        round: 'Z',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        background: 'overlay',
        color: 'title',
        fontSize: 'Z2',
        fontFamily: 'inherit',
        resize: 'vertical',
        width: '100%',
        value: (el, s) => s.root?._mentionModal?.message || '',
        onInput: (e, el, s) => {
          s.root.update({
            _mentionModal: { ...s.root._mentionModal, message: e.target.value }
          })
        }
      }
    },

    // Actions
    Actions: {
      flow: 'x',
      gap: 'Z',
      align: 'center right',

      CancelBtn: {
        extends: 'Button',
        theme: 'secondary',
        text: 'Cancel',
        onClick: (e, el, s) => s.root.update({ _mentionModal: { open: false } })
      },

      SendBtn: {
        extends: 'Button',
        theme: 'primary',
        text: 'Send message',
        onClick: async (e, el, s) => {
          const modal = s.root?._mentionModal
          if (!modal?.channelId || !modal?.message?.trim()) return
          try {
            // migrated 2026-04-27 to SDK route workspaceProject.chat.messages.create
            await getSDK().execute('workspaceProject.chat.messages', 'create', {
              channelId: modal.channelId,
              payload: {
                author_email: s.root.currentUserEmail,
                body: modal.message.trim(),
                created_at: new Date().toISOString()
              }
            })

            // Close modals and navigate to chat
            s.root.update({
              _mentionModal: { open: false },
              selectedTicket: null,
              ticketDraft: null,
              ticketEditMode: false,
              activeChannelId: modal.channelId,
              activeTab: 'conversation'
            })
            let root = el
            while (root && !root.routes) root = root.parent
            if (root) el.router('/chat', root)
          } catch (err) {
            console.error('[Mention] Send failed:', err)
          }
        }
      }
    }
  }
}

export const TicketsDetailField = {
  flow: 'y',
  gap: 'Z',
  paddingBottom: 'A',
  borderBottomWidth: '1px',
  borderBottomColor: 'overlay',
  borderBottomStyle: 'solid',
  ':last-child': { borderBottom: 'none', paddingBottom: '0' },

  CaptionTitle: {
    fontSize: 'Y2',
    Text: {
      text: (el) => el.parent?.parent?.label || ''
    }
  },

  Value: {
    tag: 'span',
    fontSize: 'Z2',
    color: 'title',
    text: (el, s) => el.parent?.value || ''
  }
}

export const TicketsDetailAssignee = {
  flow: 'x',
  align: 'center left',
  gap: 'Z',

  Avatar: {
    display: (el, s) => (s._empty ? 'none' : 'flex'),
    tag: 'img',
    width: 'B',
    height: 'B',
    round: '50%',
    borderWidth: '1.5px',
    borderColor: 'overlayStrong',
    borderStyle: 'solid',
    background: 'overlay',
    attr: {
      src: (el, s) => s.avatar,
      alt: (el, s) => s.name || ''
    }
  },

  Info: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',
    Name: {
      tag: 'span',
      fontSize: 'Z2',
      fontWeight: '500',
      color: (el, s) => (s._empty ? 'caption' : 'title'),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.name || ''
    },
    Login: {
      display: (el, s) => (s.login ? 'inline' : 'none'),
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => `@${s.login || ''}`
    }
  }
}

// ── Ticket comment row ──
export const TicketCommentRow = {
  flow: 'x',
  gap: 'Z',
  align: 'flex-start',
  padding: 'Z 0',
  borderBottomWidth: '1px',
  borderBottomColor: 'line',
  borderBottomStyle: 'solid',
  ':last-child': { borderBottom: 'none' },

  Avatar: {
    avatarType: 'initials',
    boxSize: 'A1 A1',
    flexShrink: '0',
    src: (el, s) => el.call('avatarUrl', s._name || s.author_email)
  },

  Content: {
    flow: 'y',
    gap: 'V',
    flex: '1',
    minWidth: '0',

    Header: {
      flow: 'x',
      gap: 'Z',
      align: 'center left',
      Name: {
        tag: 'span', fontSize: 'Y2', fontWeight: '600', color: 'title',
        text: (el, s) => s._name || s.author_email?.split('@')[0] || ''
      },
      Time: {
        tag: 'span', fontSize: 'Y1', color: 'caption',
        text: (el, s) => {
          if (!s.created_at) return ''
          const d = new Date(s.created_at)
          return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
            ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        }
      }
    },

    Body: {
      tag: 'div', margin: '0', fontSize: 'Z2', color: 'title',
      lineHeight: '1.5', wordBreak: 'break-word',
      onCreate: (el, s) => renderMarkdown(el, s.body || '', null, s.root && s.root.people),
      onUpdate: (el, s) => renderMarkdown(el, s.body || '', null, s.root && s.root.people)
    }
  }
}

// ── Dependencies panel ──────────────────────────────────────────
// Two lists in the modal sidebar:
//   • "Depends on"  — what this ticket is waiting for (blocks_by / waits_on_ship)
//   • "Blocks"      — tickets that depend on this one (inverse)
// Fetches via onUpdate when the currently-selected ticket id changes,
// caches result in `root._ticketDeps` keyed by ticket id to avoid
// re-fetching on every tick.

const _kindColor = (kind) => ({
  blocked_by: 'red',
  blocks: 'orange',
  waits_on_ship: 'magenta',
  related: 'cyan'
}[kind] || 'caption')

const _kindLabel = (kind) => ({
  blocked_by: 'blocked by',
  blocks: 'blocks',
  waits_on_ship: 'waits-on-ship',
  related: 'related'
}[kind] || kind)

const _stateColor = (st) => {
  if (st === 'done' || st === 'CLOSED' || st === 'closed') return 'green'
  if (st === 'in_progress') return 'yellow'
  if (st === 'blocked') return 'red'
  return 'caption'
}

export const TicketDependenciesPanel = {
  display: (el, s) => {
    const d = s.root._ticketDeps?.[s.root.selectedTicket?.id]
    return (d && (d.outgoing.length || d.incoming.length)) ? 'flex' : 'none'
  },
  flow: 'y',
  gap: 'Z',

  // onRender is one-shot and fires before selectedTicket is set (async URL-restore
  // race — see TicketsDetailModal.onCreate setInterval). onUpdate fires reactively
  // on every root state change. el._fetchedForId deduplicates per ticket id so we
  // never issue redundant requests when the root re-renders for unrelated updates.
  onUpdate: (el, s) => {
    const id = s.root?.selectedTicket?.id
    if (!id || el._fetchedForId === id) return
    el._fetchedForId = id
    const cache = s.root._ticketDeps || {}
    if (cache[id] && !cache[id]._loading) return
    // Mark loading synchronously to de-dupe concurrent renders.
    s.root._ticketDeps = { ...cache, [id]: { outgoing: [], incoming: [], _loading: true } }
    _fetchTicketDeps(id).then((deps) => {
      // Only commit if still looking at the same ticket.
      if (s.root?.selectedTicket?.id !== id) return
      s.root.update({ _ticketDeps: { ...(s.root._ticketDeps || {}), [id]: deps } })
    })
  },

  CaptionTitle: { Text: { text: 'Dependencies' } },

  DependsOn: {
    display: (el, s) => {
      const d = s.root._ticketDeps?.[s.root.selectedTicket?.id]
      return (d?.outgoing?.length) ? 'flex' : 'none'
    },
    flow: 'y',
    gap: 'Y',

    Label: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '600',
      color: 'caption',
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      text: (el, s) => {
        const d = s.root._ticketDeps?.[s.root.selectedTicket?.id]
        return `Depends on (${d?.outgoing?.length || 0})`
      }
    },

    List: {
      flow: 'y',
      gap: 'V',

      childExtends: 'TicketDependencyRow',
      childrenAs: 'state',
      children: (el, s) => s.root._ticketDeps?.[s.root.selectedTicket?.id]?.outgoing || []
    }
  },

  Blocks: {
    display: (el, s) => {
      const d = s.root._ticketDeps?.[s.root.selectedTicket?.id]
      return (d?.incoming?.length) ? 'flex' : 'none'
    },
    flow: 'y',
    gap: 'Y',
    marginTop: 'Z',

    Label: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '600',
      color: 'caption',
      letterSpacing: '.04em',
      textTransform: 'uppercase',
      text: (el, s) => {
        const d = s.root._ticketDeps?.[s.root.selectedTicket?.id]
        return `Blocks (${d?.incoming?.length || 0})`
      }
    },

    List: {
      flow: 'y',
      gap: 'V',

      childExtends: 'TicketDependencyRow',
      childrenAs: 'state',
      children: (el, s) => s.root._ticketDeps?.[s.root.selectedTicket?.id]?.incoming || []
    }
  }
}

const _isDone = (state) =>
  state === 'done' || state === 'CLOSED' || state === 'closed'

export const TicketDependencyRow = {
  flow: 'x',
  gap: 'Y',
  align: 'center left',
  padding: 'V Z',
  round: 'V',
  background: 'overlay',
  borderLeftWidth: '3px',
  borderLeftStyle: 'solid',
  borderLeftColor: (el, s) => `var(--color-${_kindColor(s.kind)})`,
  cursor: 'pointer',
  transition: 'background 0.12s ease',
  ':hover': { background: 'overlayStrong' },

  onClick: (e, el, s) => {
    _openTicketInModal(s.root || el.getRoot(), s.id, _findModalFromEl(el))
  },

  Kind: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '700',
    color: (el, s) => _kindColor(s.kind),
    text: (el, s) => _kindLabel(s.kind)
  },

  Ref: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontFamily: 'monospace',
    color: 'caption',
    text: (el, s) => s.external_id || `#${s.id}`
  },

  Title: {
    tag: 'span',
    flex: '1',
    fontSize: 'Y2',
    color: (el, s) => _isDone(s.state) ? 'caption' : 'title',
    textDecoration: (el, s) => _isDone(s.state) ? 'line-through' : 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s.title || '').replace(/^B-\d+\s*·\s*/, '')
  },

  State: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '600',
    color: (el, s) => _stateColor(s.state),
    text: (el, s) => _isDone(s.state) ? 'resolved' : formatStateLabel(s.state)
  }
}

// ── Main-column variant of dependency row (wider, full-width) ──
// Used by DependenciesSection in the scrollable Main content area.
// Shows ticket ref, title (with strikethrough when done), kind chip, state badge.
export const TicketDependencyMainRow = {
  flow: 'x',
  gap: 'Z',
  align: 'center left',
  padding: 'Z A',
  round: 'Z2',
  background: 'overlay',
  borderLeftWidth: '3px',
  borderLeftStyle: 'solid',
  borderLeftColor: (el, s) => `var(--color-${_kindColor(s.kind)})`,
  cursor: 'pointer',
  transition: 'background 0.12s ease',
  ':hover': { background: 'overlayStrong' },

  onClick: (e, el, s) => {
    _openTicketInModal(s.root || el.getRoot(), s.id, _findModalFromEl(el))
  },

  Ref: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontFamily: 'monospace',
    fontWeight: '600',
    color: 'caption',
    text: (el, s) => s.external_id || `#${s.id}`
  },

  Title: {
    tag: 'span',
    flex: '1',
    fontSize: 'Z2',
    color: (el, s) => _isDone(s.state) ? 'caption' : 'title',
    textDecoration: (el, s) => _isDone(s.state) ? 'line-through' : 'none',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => (s.title || '').replace(/^B-\d+\s*·\s*/, '')
  },

  KindChip: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y1',
    fontWeight: '700',
    letterSpacing: '.04em',
    padding: 'V Y',
    round: 'C',
    color: (el, s) => _kindColor(s.kind),
    background: (el, s) => `color-mix(in srgb, var(--color-${_kindColor(s.kind)}) 14%, transparent)`,
    text: (el, s) => _kindLabel(s.kind).toUpperCase()
  },

  StatePill: {
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y1',
    fontWeight: '700',
    letterSpacing: '.04em',
    padding: 'V Y',
    round: 'C',
    color: (el, s) => _stateColor(s.state),
    background: (el, s) => `color-mix(in srgb, var(--color-${_stateColor(s.state)}) 14%, transparent)`,
    text: (el, s) => _isDone(s.state) ? 'RESOLVED' : formatStateLabel(s.state).toUpperCase()
  }
}
